import React, {type ChangeEvent, useEffect, useState} from 'react';
import BulkAddToCart from "./BulkAddToCart";
import DraftOrderSelectorModal from "./DraftOrderSelectorModal";
import QuoteSelectorModal from "./QuoteSelectorModal";
import FavouriteButton from "./FavouriteButton";
import WarehouseSummary, {type WarehouseSummaryData} from "./WarehouseSummary";
import type {ProductSearchUiSettings, SearchResponse} from "./ProductSearchForm";
import PaginationButtons from "./PaginationButtons";
import {toast} from "react-toastify";
import SkeletonLoader from './SkeletonLoader';
import type {BreakpointLookup, ProductPricing, UnitConversion} from "../hooks/usePrice";
import type {DraftOrderItemInput, QuoteItemInput} from "../types/myaccount";
import MyAccountCard from './MyAccountCard';
import {Link, useLocation} from 'react-router-dom';
import { applyWooCartUpdates } from "../utils/applyWooCartUpdates";
import { formatMoneyWithCurrencyCode } from "../utils/formatMoneyWithCurrencyCode";
import normalizeImageSource from "../utils/normalizeImageSource";
import {
    onBackInStockTargetStateChange,
    resolveBackInStockTargetKeys,
    sanitizeBackInStockTargetKeys,
    setBackInStockTargetSubscriptionState,
} from "../utils/backInStockTargets";

interface BreakpointRow {
    min_qty: number;
    price: number;
    currency_symbol?: string;
    currency_code?: string;
    uom?: string;
}

interface SalesPriceRow {
    quantity: number;
    price: number;
    currency_symbol?: string;
    currency_code?: string;
}

interface PricingSnapshot {
    unitPrice: number;
    currencySymbol: string;
    currencyCode: string;
    nextBreakQty: number | null;
    nextBreakPrice: number | null;
}

interface SerialOption {
    serial_number: string;
    warehouse_id?: string;
    location_id?: string;
    quantity: number;
    expiration_date?: string;
    label?: string;
}

interface VariationOption {
    value: string;
    label: string;
}

interface VariationAttribute {
    key: string;
    name?: string;
    label?: string;
    options: VariationOption[];
}

interface ProductVariation {
    id: number;
    attributes: Record<string, string>;
    isInStock?: boolean;
    isPurchasable?: boolean;
    pricingUnavailableMessage?: string;
    currencyCode?: string;
    price?: number;
    regularPrice?: number;
    rrp?: number;
    basePrice?: number;
    salesPrices?: ProductPricing | null;
    breakpointLookup?: BreakpointLookup;
    baseUom?: string;
    uomEnabled?: boolean;
    availableUoms?: string[];
    unitConversions?: UnitConversion[];
    inventoryPolicy?: InventoryPolicy;
    inventoryPolicyByUom?: Record<string, InventoryPolicy>;
    serialEnabled?: boolean;
    serialForceBuyAll?: boolean;
    serialDisplayMode?: string;
    serialOptionsEndpoint?: string;
    stockStatus?: string;
    stockQuantity?: number | null;
    isOnBackorder?: boolean;
    backordersAllowed?: boolean;
}

interface VariationSelectionResult {
    isVariableProduct: boolean;
    variationAttributes: VariationAttribute[];
    availableVariations: ProductVariation[];
    selectedAttributes: Record<string, string>;
    selectedPayload: Record<string, string>;
    isSelectionComplete: boolean;
    selectedVariation: ProductVariation | null;
    isSelectionValid: boolean;
    selectionMessage: string;
}

interface InventoryPolicy {
    selected_uom?: string;
    min_qty?: number;
    min_non_zero_qty?: number;
    step_qty?: number;
    max_qty?: number | null;
    allow_zero?: boolean;
    is_decimal_allowed?: boolean;
    step_mode_enabled?: boolean;
    can_purchase?: boolean;
    stock_fully_reserved_in_cart?: boolean;
    backorders_allowed?: boolean;
    backorders_require_notification?: boolean;
    is_on_backorder?: boolean;
    normalized_status?: string;
    status_label?: string;
}

interface QuantityClampResult {
    quantity: number;
    adjusted: boolean;
    reason: string;
}

interface SavedItemPayloadContext {
    payload: DraftOrderItemInput;
    unitPrice: number;
}

export interface Product {
    id: number;
    slug?: string;
    name: string;
    sku?: string;
    type: string;
    rrp: string;
    price: string;
    compare_price?: string | number;
    compare_price_source?: string;
    url: string;
    image: string | null;
    placeholder_image?: string;
    stock: number | null;
    in_stock: boolean;
    is_purchasable?: boolean;
    is_enquiry_product?: boolean;
    enquiry_button_text?: string;
    can_add_to_cart?: boolean;
    backorders_allowed?: boolean;
    is_on_backorder?: boolean;
    cart_qty: number;
    pricing?: ProductPricing | null;
    breakpoint_lookup?: BreakpointLookup;
    uom?: string;
    cart_uom?: string;
    base_uom?: string;
    available_uoms?: string[];
    unit_conversions?: UnitConversion[];
    inventory_policy?: InventoryPolicy;
    inventory_policy_by_uom?: Record<string, InventoryPolicy>;
    pricing_enabled?: boolean;
    uom_enabled?: boolean;
    quantity_breaks_enabled?: boolean;
    show_quantity_breaks_table?: boolean;
    hide_calculated_price?: boolean;
    currency_symbol?: string;
    currency_code?: string;
    tax_display_mode?: string;
    prices_include_tax?: boolean;
    price_display_tax_enabled?: boolean;
    price_display_multiplier?: string | number;
    price_including_tax_multiplier?: string | number;
    price_excluding_tax_multiplier?: string | number;
    price_display_suffix?: string;
    price_display_suffix_template?: string;
    pricing_unavailable_message?: string;
    serial_enabled?: boolean;
    serial_force_buy_all?: boolean;
    serial_display_mode?: string;
    serial_options_endpoint?: string;
    is_variable_product?: boolean;
    variation_attributes?: VariationAttribute[];
    available_variations?: ProductVariation[];
    default_variation_attributes?: Record<string, string>;
    categories?: string[];
    description?: string;
    product_files?: ProductFile[];
    is_favourite?: boolean;
    warehouse_summary?: WarehouseSummaryData | null;
    back_in_stock_target_keys?: string[];
}

export interface ProductFile {
    guid: string;
    attachment_id: number;
    title: string;
    url: string;
    mime_type: string;
    extension: string;
    file_size?: string;
    status?: 'ok' | 'missing_attachment';
}

export interface ProductResultTableProps {
    searchResponse: SearchResponse | null,
    onSort: (sort: string) => void,
    sort: string,
    templateCsvUrl: string,
    onPageChange: (toPage: number) => void,
    detailMode?: boolean,
    productDetailBasePath?: string,
    showToolbar?: boolean,
    embedded?: boolean,
    emptyMessage?: string,
    fallbackProductLinksOpenInNewTab?: boolean,
    uiSettings?: ProductSearchUiSettings,
    compactDesktop?: boolean,
}

const normalizeUom = (value: string | undefined | null): string => String(value || '').trim().toUpperCase();
const getSerialOptionKey = (option: SerialOption): string => {
    return [
        String(option.serial_number || '').trim(),
        String(option.warehouse_id || '').trim(),
        String(option.location_id || '').trim(),
    ].join('|');
};

const normalizeSerialOption = (raw: any): SerialOption | null => {
    if (!raw || typeof raw !== 'object') {
        return null;
    }

    const serialNumber = String(raw.serial_number || '').trim();
    if (serialNumber === '') {
        return null;
    }

    const quantity = Number(raw.quantity || 0);
    const safeQuantity = Number.isFinite(quantity) ? Math.max(0, quantity) : 0;

    return {
        serial_number: serialNumber,
        warehouse_id: String(raw.warehouse_id || '').trim(),
        location_id: String(raw.location_id || '').trim(),
        quantity: safeQuantity,
        expiration_date: String(raw.expiration_date || '').trim(),
        label: String(raw.label || '').trim(),
    };
};

const isTruthy = (value: unknown): boolean => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    const normalized = String(value || '').trim().toLowerCase();
    return ['1', 'true', 'yes', 'on'].includes(normalized);
};

const normalizeVariationAttributeKey = (value: unknown): string => {
    const raw = String(value || '').trim().toLowerCase();
    if (raw === '') {
        return '';
    }

    if (raw.startsWith('attribute_')) {
        return raw;
    }

    return `attribute_${raw}`;
};

const normalizeVariationValue = (value: unknown): string => String(value || '').trim();

const normalizeVariationAttributesMap = (raw: unknown): Record<string, string> => {
    if (!raw || typeof raw !== 'object') {
        return {};
    }

    const normalized: Record<string, string> = {};
    Object.entries(raw as Record<string, unknown>).forEach(([key, value]) => {
        const normalizedKey = normalizeVariationAttributeKey(key);
        if (normalizedKey === '') {
            return;
        }

        normalized[normalizedKey] = normalizeVariationValue(value);
    });

    return normalized;
};

const normalizeVariationAttribute = (raw: unknown): VariationAttribute | null => {
    if (!raw || typeof raw !== 'object') {
        return null;
    }

    const source = raw as Record<string, unknown>;
    const key = normalizeVariationAttributeKey(source.key ?? source.name);
    if (key === '') {
        return null;
    }

    const name = String(source.name || '').trim();
    const fallbackLabel = key.replace(/^attribute_/, '').replace(/^pa_/, '').replace(/[_-]+/g, ' ').trim();
    const label = String(source.label || name || fallbackLabel || key).trim();

    const options: VariationOption[] = [];
    const seen = new Set<string>();
    const rawOptions = Array.isArray(source.options) ? source.options : [];
    rawOptions.forEach((option) => {
        if (option && typeof option === 'object') {
            const optionRow = option as Record<string, unknown>;
            const optionValue = normalizeVariationValue(optionRow.value);
            if (optionValue === '' || seen.has(optionValue)) {
                return;
            }

            seen.add(optionValue);
            const optionLabel = String(optionRow.label || optionValue).trim();
            options.push({
                value: optionValue,
                label: optionLabel !== '' ? optionLabel : optionValue,
            });
            return;
        }

        const optionValue = normalizeVariationValue(option);
        if (optionValue === '' || seen.has(optionValue)) {
            return;
        }

        seen.add(optionValue);
        options.push({
            value: optionValue,
            label: optionValue,
        });
    });

    return {
        key,
        name,
        label,
        options,
    };
};

const normalizeProductPricing = (raw: unknown): ProductPricing | null => {
    if (!raw || typeof raw !== 'object') {
        return null;
    }

    const source = raw as Record<string, unknown>;
    const pricing = source.pricing;
    const quantityBreaks = source.quantity_breaks;
    if ((!pricing || typeof pricing !== 'object') && (!quantityBreaks || typeof quantityBreaks !== 'object')) {
        return null;
    }

    return {
        pricing: (pricing && typeof pricing === 'object') ? (pricing as ProductPricing['pricing']) : {},
        quantity_breaks: (quantityBreaks && typeof quantityBreaks === 'object')
            ? (quantityBreaks as ProductPricing['quantity_breaks'])
            : {},
    };
};

const normalizeBreakpointLookup = (raw: unknown): BreakpointLookup | undefined => {
    if (!raw || typeof raw !== 'object') {
        return undefined;
    }

    return raw as BreakpointLookup;
};

const normalizeInventoryPolicy = (raw: unknown): InventoryPolicy | undefined => {
    if (!raw || typeof raw !== 'object') {
        return undefined;
    }

    return raw as InventoryPolicy;
};

const normalizeInventoryPolicyMap = (raw: unknown): Record<string, InventoryPolicy> | undefined => {
    if (!raw || typeof raw !== 'object') {
        return undefined;
    }

    return raw as Record<string, InventoryPolicy>;
};

const normalizeAvailableUoms = (raw: unknown): string[] => {
    if (!Array.isArray(raw)) {
        return [];
    }

    const seen = new Set<string>();
    const normalized: string[] = [];
    raw.forEach((entry) => {
        const value = normalizeUom(String(entry || ''));
        if (value !== '' && !seen.has(value)) {
            seen.add(value);
            normalized.push(value);
        }
    });

    return normalized;
};

const normalizeUnitConversions = (raw: unknown): UnitConversion[] => {
    if (!Array.isArray(raw)) {
        return [];
    }

    return raw
        .filter((entry) => entry && typeof entry === 'object')
        .map((entry) => entry as UnitConversion);
};

const normalizeVariation = (raw: unknown): ProductVariation | null => {
    if (!raw || typeof raw !== 'object') {
        return null;
    }

    const source = raw as Record<string, unknown>;
    const id = Number(source.id ?? source.variation_id ?? 0);
    if (!Number.isFinite(id) || id <= 0) {
        return null;
    }

    const parsedStockQuantity = source.stockQuantity ?? source.stock_quantity;
    const stockQuantity = parsedStockQuantity === undefined || parsedStockQuantity === null
        ? null
        : Number(parsedStockQuantity);

    return {
        id,
        attributes: normalizeVariationAttributesMap(source.attributes),
        isInStock: source.isInStock === undefined ? undefined : isTruthy(source.isInStock),
        isPurchasable: source.isPurchasable === undefined ? undefined : isTruthy(source.isPurchasable),
        pricingUnavailableMessage: String(source.pricingUnavailableMessage ?? source.pricing_unavailable_message ?? '').trim(),
        currencyCode: String(source.currencyCode ?? source.currency_code ?? '').trim().toUpperCase(),
        price: Number(source.price ?? 0),
        regularPrice: Number(source.regularPrice ?? source.regular_price ?? 0),
        rrp: Number(source.rrp ?? source.regularPrice ?? source.regular_price ?? 0),
        basePrice: Number(source.basePrice ?? source.base_price ?? source.price ?? 0),
        salesPrices: normalizeProductPricing(source.salesPrices ?? source.sales_prices),
        breakpointLookup: normalizeBreakpointLookup(source.breakpointLookup ?? source.breakpoint_lookup),
        baseUom: normalizeUom(String(source.baseUom ?? source.base_uom ?? 'EA')) || 'EA',
        uomEnabled: source.uomEnabled === undefined ? undefined : isTruthy(source.uomEnabled),
        availableUoms: normalizeAvailableUoms(source.availableUoms ?? source.available_uoms),
        unitConversions: normalizeUnitConversions(source.unitConversions ?? source.unit_conversions),
        inventoryPolicy: normalizeInventoryPolicy(source.inventoryPolicy ?? source.inventory_policy),
        inventoryPolicyByUom: normalizeInventoryPolicyMap(source.inventoryPolicyByUom ?? source.inventory_policy_by_uom),
        serialEnabled: source.serialEnabled === undefined ? undefined : isTruthy(source.serialEnabled),
        serialForceBuyAll: source.serialForceBuyAll === undefined ? undefined : isTruthy(source.serialForceBuyAll),
        serialDisplayMode: String(source.serialDisplayMode ?? source.serial_display_mode ?? '').trim(),
        serialOptionsEndpoint: String(source.serialOptionsEndpoint ?? source.serial_options_endpoint ?? '').trim(),
        stockStatus: String(source.stockStatus ?? source.stock_status ?? '').trim(),
        stockQuantity: Number.isFinite(stockQuantity as number) ? Number(stockQuantity) : null,
        isOnBackorder: source.isOnBackorder === undefined ? undefined : isTruthy(source.isOnBackorder),
        backordersAllowed: source.backordersAllowed === undefined
            ? (source.backorders_allowed === undefined ? undefined : isTruthy(source.backorders_allowed))
            : isTruthy(source.backordersAllowed),
    };
};

const getVariationAttributesForProduct = (product: Product): VariationAttribute[] => {
    const rawAttributes = Array.isArray(product.variation_attributes) ? product.variation_attributes : [];
    return rawAttributes
        .map((attribute) => normalizeVariationAttribute(attribute))
        .filter((attribute): attribute is VariationAttribute => attribute !== null);
};

const getAvailableVariationsForProduct = (product: Product): ProductVariation[] => {
    const rawVariations = Array.isArray(product.available_variations) ? product.available_variations : [];
    return rawVariations
        .map((variation) => normalizeVariation(variation))
        .filter((variation): variation is ProductVariation => variation !== null);
};

const getDefaultVariationSelection = (product: Product, variationAttributes: VariationAttribute[]): Record<string, string> => {
    const defaults = normalizeVariationAttributesMap(product.default_variation_attributes || {});
    const selection: Record<string, string> = {};

    variationAttributes.forEach((attribute) => {
        const optionSet = new Set(attribute.options.map((option) => normalizeVariationValue(option.value)));
        const defaultValue = normalizeVariationValue(defaults[attribute.key]);
        selection[attribute.key] = (defaultValue !== '' && (optionSet.size === 0 || optionSet.has(defaultValue)))
            ? defaultValue
            : '';
    });

    return selection;
};

const isVariableProduct = (product: Product, variationAttributes: VariationAttribute[], availableVariations: ProductVariation[]): boolean => {
    return product.is_variable_product === true || variationAttributes.length > 0 || availableVariations.length > 0;
};

const resolveVariationSelection = (product: Product, selectedAttributesInput?: Record<string, string>): VariationSelectionResult => {
    const variationAttributes = getVariationAttributesForProduct(product);
    const availableVariations = getAvailableVariationsForProduct(product);
    const variableProduct = isVariableProduct(product, variationAttributes, availableVariations);
    const defaultSelection = getDefaultVariationSelection(product, variationAttributes);

    const selectedAttributes: Record<string, string> = {};
    variationAttributes.forEach((attribute) => {
        const optionSet = new Set(attribute.options.map((option) => normalizeVariationValue(option.value)));
        const requestedValue = normalizeVariationValue(selectedAttributesInput?.[attribute.key]);
        if (requestedValue !== '' && (optionSet.size === 0 || optionSet.has(requestedValue))) {
            selectedAttributes[attribute.key] = requestedValue;
            return;
        }

        const defaultValue = normalizeVariationValue(defaultSelection[attribute.key]);
        if (defaultValue !== '' && (optionSet.size === 0 || optionSet.has(defaultValue))) {
            selectedAttributes[attribute.key] = defaultValue;
            return;
        }

        selectedAttributes[attribute.key] = '';
    });

    const selectedPayload: Record<string, string> = {};
    variationAttributes.forEach((attribute) => {
        const value = normalizeVariationValue(selectedAttributes[attribute.key]);
        if (value !== '') {
            selectedPayload[attribute.key] = value;
        }
    });

    const isSelectionComplete = !variableProduct || variationAttributes.every((attribute) => (
        normalizeVariationValue(selectedPayload[attribute.key]) !== ''
    ));

    const matchingVariations = !variableProduct
        ? []
        : availableVariations.filter((variation) => {
            return variationAttributes.every((attribute) => {
                const selectedValue = normalizeVariationValue(selectedPayload[attribute.key]);
                if (selectedValue === '') {
                    return true;
                }

                const variationValue = normalizeVariationValue(variation.attributes[attribute.key]);
                return variationValue === '' || variationValue === selectedValue;
            });
        });

    let selectedVariation: ProductVariation | null = null;
    if (variableProduct && isSelectionComplete && matchingVariations.length > 0) {
        selectedVariation = matchingVariations.find((variation) => (
            variation.isInStock !== false && variation.isPurchasable !== false
        )) || matchingVariations[0] || null;
    }

    const isSelectionValid = !variableProduct || (
        isSelectionComplete
        && selectedVariation !== null
        && selectedVariation.isInStock !== false
        && selectedVariation.isPurchasable !== false
    );

    let selectionMessage = '';
    if (variableProduct && variationAttributes.length > 0) {
        if (!isSelectionComplete) {
            selectionMessage = 'Select product options.';
        } else if (!selectedVariation || selectedVariation.isInStock === false || selectedVariation.isPurchasable === false) {
            selectionMessage = 'Selected variation is unavailable.';
        }
    }

    return {
        isVariableProduct: variableProduct,
        variationAttributes,
        availableVariations,
        selectedAttributes,
        selectedPayload,
        isSelectionComplete,
        selectedVariation,
        isSelectionValid,
        selectionMessage,
    };
};

const isVariationOptionSelectable = (
    variationState: VariationSelectionResult,
    attributeKey: string,
    optionValue: string
): boolean => {
    if (!variationState.isVariableProduct || variationState.availableVariations.length === 0) {
        return true;
    }

    const normalizedAttributeKey = normalizeVariationAttributeKey(attributeKey);
    const normalizedOptionValue = normalizeVariationValue(optionValue);
    if (normalizedAttributeKey === '' || normalizedOptionValue === '') {
        return true;
    }

    return variationState.availableVariations.some((variation) => {
        if (variation.isInStock === false || variation.isPurchasable === false) {
            return false;
        }

        return variationState.variationAttributes.every((attribute) => {
            const targetValue = attribute.key === normalizedAttributeKey
                ? normalizedOptionValue
                : normalizeVariationValue(variationState.selectedPayload[attribute.key]);
            if (targetValue === '') {
                return true;
            }

            const variationValue = normalizeVariationValue(variation.attributes[attribute.key]);
            return variationValue === '' || variationValue === targetValue;
        });
    });
};

const getActiveProductForVariation = (product: Product, variation: ProductVariation | null): Product => {
    if (!variation) {
        return product;
    }

    const variationBasePrice = Number(variation.basePrice ?? variation.price ?? product.price);
    const stockQuantity = variation.stockQuantity;
    const canAddToCart = variation.isInStock !== false
        && variation.isPurchasable !== false
        && (product.can_add_to_cart !== false);

    return {
        ...product,
        rrp: Number.isFinite(Number(variation.rrp))
            ? String(Number(variation.rrp))
            : (Number.isFinite(Number(variation.regularPrice)) ? String(Number(variation.regularPrice)) : product.rrp),
        price: Number.isFinite(variationBasePrice) ? String(variationBasePrice) : product.price,
        stock: typeof stockQuantity === 'number' && Number.isFinite(stockQuantity) ? stockQuantity : product.stock,
        in_stock: variation.isInStock === undefined ? product.in_stock : variation.isInStock,
        is_purchasable: variation.isPurchasable === undefined ? product.is_purchasable : variation.isPurchasable,
        can_add_to_cart: canAddToCart,
        backorders_allowed: variation.backordersAllowed === undefined ? product.backorders_allowed : variation.backordersAllowed,
        is_on_backorder: variation.isOnBackorder === undefined ? product.is_on_backorder : variation.isOnBackorder,
        pricing: variation.salesPrices ?? product.pricing,
        breakpoint_lookup: variation.breakpointLookup ?? product.breakpoint_lookup,
        base_uom: normalizeUom(variation.baseUom || product.base_uom || 'EA') || 'EA',
        available_uoms: (variation.availableUoms && variation.availableUoms.length > 0) ? variation.availableUoms : product.available_uoms,
        unit_conversions: (variation.unitConversions && variation.unitConversions.length > 0) ? variation.unitConversions : product.unit_conversions,
        inventory_policy: variation.inventoryPolicy ?? product.inventory_policy,
        inventory_policy_by_uom: variation.inventoryPolicyByUom ?? product.inventory_policy_by_uom,
        uom_enabled: variation.uomEnabled === undefined ? product.uom_enabled : variation.uomEnabled,
        serial_enabled: variation.serialEnabled === undefined ? product.serial_enabled : variation.serialEnabled,
        serial_force_buy_all: variation.serialForceBuyAll === undefined ? product.serial_force_buy_all : variation.serialForceBuyAll,
        serial_display_mode: variation.serialDisplayMode || product.serial_display_mode,
        serial_options_endpoint: variation.serialOptionsEndpoint || product.serial_options_endpoint,
    };
};

const buildSerialOptionsEndpointForProduct = (endpoint: string, productId: number): string => {
    const normalizedEndpoint = String(endpoint || '').trim();
    if (normalizedEndpoint === '' || productId <= 0) {
        return '';
    }

    try {
        const url = new URL(normalizedEndpoint, window.location.origin);
        url.pathname = url.pathname.replace(/serialnumber_stock_options\/\d+/, `serialnumber_stock_options/${productId}`);
        return `${url.pathname}${url.search}`;
    } catch {
        return normalizedEndpoint.replace(/serialnumber_stock_options\/\d+/, `serialnumber_stock_options/${productId}`);
    }
};

const isSerialEnabled = (product: Product): boolean => product.serial_enabled === true;
const isSerialForceBuyAll = (product: Product): boolean => product.serial_force_buy_all === true;
const getSerialDisplayMode = (product: Product): 'dropdown' | 'table' => {
    const mode = String(product.serial_display_mode || 'dropdown').trim().toLowerCase();
    return mode === 'table' ? 'table' : 'dropdown';
};
const decodeHtmlEntities = (value: string): string => {
    const raw = String(value || '');
    if (raw === '' || raw.indexOf('&') === -1) {
        return raw;
    }

    if (typeof document === 'undefined') {
        return raw
            .replace(/&#36;/g, '$')
            .replace(/&amp;/g, '&')
            .replace(/&pound;/g, '£')
            .replace(/&euro;/g, '€')
            .replace(/&yen;/g, '¥');
    }

    const decoder = document.createElement('textarea');
    decoder.innerHTML = raw;
    return decoder.value;
};

const stripHtmlForPreview = (value: string): string => {
    const raw = String(value || '').trim();
    if (raw === '') {
        return '';
    }

    if (typeof document !== 'undefined') {
        const template = document.createElement('template');
        template.innerHTML = raw;
        return decodeHtmlEntities(template.content.textContent || '')
            .replace(/\s+/g, ' ')
            .trim();
    }

    return decodeHtmlEntities(raw.replace(/<[^>]*>/g, ' '))
        .replace(/\s+/g, ' ')
        .trim();
};

const truncatePreview = (value: string, limit: number): string => {
    const normalized = stripHtmlForPreview(value);
    if (normalized.length <= limit) {
        return normalized;
    }

    return `${normalized.slice(0, limit).trimEnd()}...`;
};

const normalizeCurrencySymbol = (value: string | undefined | null): string => {
    return decodeHtmlEntities(String(value || '')).trim();
};

const formatQuantity = (value: number, decimalAllowed: boolean): string => {
    if (!Number.isFinite(value)) {
        return '0';
    }

    if (!decimalAllowed) {
        return String(Math.max(0, Math.round(value)));
    }

    const rounded = Math.round(value * 100) / 100;
    if (Math.abs(rounded - Math.round(rounded)) < 0.000001) {
        return String(Math.round(rounded));
    }

    return rounded.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
};

const normalizeQuantity = (value: number, decimalAllowed: boolean): number => {
    const safe = Number.isFinite(value) ? Math.max(0, value) : 0;
    if (!decimalAllowed) {
        return Math.max(0, Math.floor(safe + 0.000001));
    }

    return Math.floor((safe * 100) + 0.000001) / 100;
};

const resolveInventoryPolicyForUom = (product: Product, uom: string): InventoryPolicy => {
    const normalizedUom = normalizeUom(uom);
    const policyByUom = product.inventory_policy_by_uom || {};
    if (normalizedUom !== '' && policyByUom[normalizedUom]) {
        return policyByUom[normalizedUom];
    }

    return product.inventory_policy || {};
};

const getPolicyStep = (policy: InventoryPolicy): number => {
    const decimalAllowed = policy.is_decimal_allowed === true;
    const fallback = decimalAllowed ? 0.01 : 1;
    const raw = Number(policy.step_qty ?? fallback);
    if (!Number.isFinite(raw) || raw <= 0) {
        return fallback;
    }

    if (!decimalAllowed) {
        return Math.max(1, Math.round(raw));
    }

    return normalizeQuantity(raw, true);
};

const getPolicyMinNonZero = (policy: InventoryPolicy): number => {
    const decimalAllowed = policy.is_decimal_allowed === true;
    const step = getPolicyStep(policy);
    const raw = Number(policy.min_non_zero_qty ?? policy.min_qty ?? step);
    if (!Number.isFinite(raw) || raw <= 0) {
        return step;
    }

    return Math.max(step, normalizeQuantity(raw, decimalAllowed));
};

const getPolicyMax = (policy: InventoryPolicy): number | null => {
    const rawValue = policy.max_qty;
    if (rawValue === null || typeof rawValue === 'undefined' || rawValue === '') {
        return null;
    }

    const raw = Number(rawValue);
    if (!Number.isFinite(raw)) {
        return null;
    }

    return Math.max(0, raw);
};

const clampQuantityToPolicy = (requested: number, policy: InventoryPolicy): QuantityClampResult => {
    const decimalAllowed = policy.is_decimal_allowed === true;
    const allowZero = policy.allow_zero === true;
    const step = getPolicyStep(policy);
    const minQty = allowZero ? 0 : getPolicyMinNonZero(policy);
    const minNonZero = getPolicyMinNonZero(policy);
    const maxQty = getPolicyMax(policy);
    const epsilon = 0.000001;

    const safeRequested = Number.isFinite(requested) ? Math.max(0, requested) : 0;
    let quantity = normalizeQuantity(safeRequested, decimalAllowed);
    let reason = 'none';

    if (!decimalAllowed && Math.abs(quantity - safeRequested) > epsilon) {
        reason = 'decimal_not_allowed';
    }

    if (quantity > epsilon) {
        const positiveMin = allowZero ? minNonZero : minQty;
        if (quantity + epsilon < positiveMin) {
            quantity = positiveMin;
            reason = 'below_minimum';
        }

        if (step > epsilon) {
            let steppedQuantity = quantity;
            if (allowZero) {
                const multiples = Math.max(1, Math.round(quantity / step));
                steppedQuantity = multiples * step;
            } else {
                const base = positiveMin;
                const offset = Math.max(0, quantity - base);
                const multiples = Math.max(0, Math.round(offset / step));
                steppedQuantity = base + (multiples * step);
            }

            if (Math.abs(steppedQuantity - quantity) > epsilon) {
                quantity = steppedQuantity;
                if (reason === 'none') {
                    reason = 'invalid_step';
                }
            }
        }
    } else {
        quantity = 0;
        if (reason === 'none') {
            reason = 'zero_quantity';
        }
    }

    if (maxQty !== null && quantity > maxQty + epsilon) {
        if (allowZero) {
            const maxMultiples = Math.floor((maxQty / step) + epsilon);
            quantity = maxMultiples > 0 ? maxMultiples * step : 0;
        } else {
            quantity = maxQty;
        }
        reason = 'above_maximum';
    }

    if (maxQty !== null && maxQty <= epsilon && safeRequested > epsilon) {
        quantity = 0;
        reason = 'out_of_stock';
    }

    quantity = normalizeQuantity(quantity, decimalAllowed);
    const adjusted = Math.abs(quantity - safeRequested) > epsilon;

    return {
        quantity,
        adjusted,
        reason: adjusted ? reason : 'none',
    };
};

const buildPolicyMessage = (
    result: QuantityClampResult,
    policy: InventoryPolicy,
    uom: string,
    showStock: boolean = true
): string => {
    const decimalAllowed = policy.is_decimal_allowed === true;
    const step = getPolicyStep(policy);
    const maxQty = getPolicyMax(policy);

    switch (result.reason) {
        case 'below_minimum':
            if (policy.step_mode_enabled === true) {
                return `MOQ increments enforced. Quantity adjusted to ${formatQuantity(result.quantity, decimalAllowed)}.`;
            }
            return `Quantity adjusted to ${formatQuantity(result.quantity, decimalAllowed)}.`;
        case 'invalid_step':
            return `Quantity adjusted to increments of ${formatQuantity(step, decimalAllowed)} ${uom}.`;
        case 'above_maximum':
            if (!showStock) {
                return 'Requested quantity is not currently available.';
            }
            if ((maxQty ?? 0) <= 0.000001 && policy.stock_fully_reserved_in_cart === true) {
                return 'All available stock is already in your cart.';
            }
            if (maxQty !== null) {
                return `Quantity adjusted to max ${formatQuantity(maxQty, decimalAllowed)} ${uom}.`;
            }
            return `Quantity adjusted to ${formatQuantity(result.quantity, decimalAllowed)}.`;
        case 'out_of_stock':
            if (!showStock) {
                return 'This item is not currently available.';
            }
            if (policy.stock_fully_reserved_in_cart === true) {
                return 'All available stock is already in your cart.';
            }
            return 'No stock available.';
        case 'decimal_not_allowed':
            return 'Whole quantities only for this product.';
        default:
            return `Quantity adjusted to ${formatQuantity(result.quantity, decimalAllowed)}.`;
    }
};

const IMAGE_TRANSPARENT_FALLBACK = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';

const buildApiEndpoint = (path: string): string => {
    if (typeof window === 'undefined') {
        return `/wp-json/weka/v1/${path.replace(/^\/+/, '')}`;
    }

    const apiBaseRaw = String((window as any)?.wekaApiSettings?.api_endpoint_base || 'weka/v1');
    const apiBase = apiBaseRaw.replace(/^\/+|\/+$/g, '');
    const normalizedPath = path.replace(/^\/+/, '');

    return `/wp-json/${apiBase}/${normalizedPath}`;
};

const buildBackInStockTargetKey = (productId: number, variationId: number): string => {
    if (productId <= 0) {
        return '';
    }

    return `${productId}:${Math.max(0, variationId)}`;
};

const hasOutOfStockStatus = (policy: InventoryPolicy | undefined): boolean => {
    const normalizedStatus = String(policy?.normalized_status || '').trim().toLowerCase();
    return normalizedStatus === 'out_of_stock' || normalizedStatus === 'outofstock';
};

const canAddToCartForProduct = (product: Product): boolean => {
    if (typeof product.can_add_to_cart === 'boolean') {
        return product.can_add_to_cart;
    }

    const isPurchasable = typeof product.is_purchasable === 'boolean'
        ? product.is_purchasable
        : true;
    return isPurchasable && product.in_stock;
};

const shouldOfferBackInStockForProduct = ({
    featureEnabled,
    variationState,
    activeProduct,
    inventoryPolicy,
}: {
    featureEnabled: boolean;
    variationState: VariationSelectionResult;
    activeProduct: Product;
    inventoryPolicy: InventoryPolicy;
}): boolean => {
    if (!featureEnabled || activeProduct.is_purchasable === false) {
        return false;
    }

    const normalizedStatus = String(inventoryPolicy.normalized_status || '').trim().toLowerCase();
    if (
        inventoryPolicy.stock_fully_reserved_in_cart === true
        || inventoryPolicy.backorders_allowed === true
        || inventoryPolicy.is_on_backorder === true
        || normalizedStatus === 'backorder'
        || inventoryPolicy.can_purchase === true
    ) {
        return false;
    }

    if (variationState.isVariableProduct) {
        const selectedVariation = variationState.selectedVariation;
        return variationState.isSelectionComplete
            && selectedVariation !== null
            && selectedVariation.id > 0
            && selectedVariation.isPurchasable !== false
            && (
                selectedVariation.isInStock === false
                || hasOutOfStockStatus(inventoryPolicy)
            );
    }

    return hasOutOfStockStatus(inventoryPolicy) && !canAddToCartForProduct(activeProduct);
};

const getProductBaseUom = (product: Product): string => {
    return normalizeUom(product.base_uom) || 'EA';
};

const getAvailableUoms = (product: Product): string[] => {
    const merged: string[] = [];
    const pushIfMissing = (uom: string) => {
        const normalized = normalizeUom(uom);
        if (normalized !== '' && !merged.includes(normalized)) {
            merged.push(normalized);
        }
    };

    pushIfMissing('EA');
    pushIfMissing(product.uom || '');

    for (const uom of product.available_uoms || []) {
        pushIfMissing(uom);
    }

    for (const uom of Object.keys(product.inventory_policy_by_uom || {})) {
        pushIfMissing(uom);
    }

    if (merged.length === 0) {
        return ['EA'];
    }

    return merged;
};

const getDefaultUomForProduct = (product: Product): string => {
    const availableUoms = getAvailableUoms(product);
    const cartUom = normalizeUom(product.cart_uom);
    if (cartUom !== '' && availableUoms.includes(cartUom)) {
        return cartUom;
    }

    const productUom = normalizeUom(product.uom);
    if (productUom !== '' && availableUoms.includes(productUom)) {
        return productUom;
    }

    return availableUoms[0] || 'EA';
};

const getConversionFactor = (product: Product, fromUom: string, toUom: string): number | null => {
    const fromNormalized = normalizeUom(fromUom);
    const toNormalized = normalizeUom(toUom);
    for (const conversion of product.unit_conversions || []) {
        const conversionFrom = normalizeUom(conversion.from);
        const conversionTo = normalizeUom(conversion.to);
        const factor = Number(conversion.factor || 0);
        if (conversionFrom === fromNormalized && conversionTo === toNormalized && factor > 0) {
            return factor;
        }
    }

    return null;
};

const getSalesRowsForUom = (product: Product, uom: string): SalesPriceRow[] => {
    const normalizedUom = normalizeUom(uom);
    const rows = product.pricing?.pricing?.[normalizedUom];
    if (!rows || typeof rows !== 'object') {
        return [];
    }

    const mappedRows = Object.values(rows).map((row: any) => ({
        quantity: Math.max(1, Number(row?.quantity || 1)),
        price: Number(row?.price || 0),
        currency_symbol: String(row?.currency_symbol || ''),
        currency_code: String(row?.currency_code || '').trim().toUpperCase(),
    })).filter((row) => Number.isFinite(row.price) && row.price > 0);

    mappedRows.sort((a, b) => a.quantity - b.quantity);
    return mappedRows;
};

const getBreakpointRowsForUom = (product: Product, uom: string): BreakpointRow[] => {
    const normalizedUom = normalizeUom(uom);
    const directRows = (product.breakpoint_lookup?.[normalizedUom] || []).map((row) => ({
        min_qty: Math.max(1, Number(row.min_qty || 1)),
        price: Number(row.price || 0),
        currency_symbol: String(row.currency_symbol || ''),
        currency_code: String(row.currency_code || '').trim().toUpperCase(),
        uom: normalizeUom(row.uom || normalizedUom),
    })).filter((row) => Number.isFinite(row.price) && row.price > 0);

    directRows.sort((a, b) => a.min_qty - b.min_qty);
    if (directRows.length > 0) {
        return directRows;
    }

    const baseUom = getProductBaseUom(product);
    if (normalizedUom === baseUom) {
        return [];
    }

    const baseRows = (product.breakpoint_lookup?.[baseUom] || []).map((row) => ({
        min_qty: Math.max(1, Number(row.min_qty || 1)),
        price: Number(row.price || 0),
        currency_symbol: String(row.currency_symbol || ''),
        currency_code: String(row.currency_code || '').trim().toUpperCase(),
        uom: normalizeUom(row.uom || baseUom),
    })).filter((row) => Number.isFinite(row.price) && row.price > 0);

    if (baseRows.length === 0) {
        return [];
    }

    const factor = getConversionFactor(product, normalizedUom, baseUom);
    if (factor === null) {
        return [];
    }

    return baseRows.map((row) => ({
        ...row,
        price: row.price * factor,
        uom: normalizedUom,
    }));
};

const getPricingSnapshotForUom = (
    product: Product,
    quantity: number,
    uom: string,
    allowConversion: boolean = true
): PricingSnapshot => {
    const normalizedQty = Math.max(1, quantity);
    const normalizedUom = normalizeUom(uom) || 'EA';
    const defaultSymbol = normalizeCurrencySymbol(product.currency_symbol);
    const defaultCurrencyCode = String(product.currency_code || '').trim().toUpperCase();
    const defaultUnitPrice = Number(product.price || 0);

    if (product.pricing_enabled !== true) {
        return {
            unitPrice: defaultUnitPrice,
            currencySymbol: defaultSymbol,
            currencyCode: defaultCurrencyCode,
            nextBreakQty: null,
            nextBreakPrice: null,
        };
    }

    if (product.quantity_breaks_enabled !== true) {
        if (allowConversion) {
            const baseUom = getProductBaseUom(product);
            if (normalizedUom !== baseUom) {
                const factor = getConversionFactor(product, normalizedUom, baseUom);
                if (factor !== null) {
                    return {
                        unitPrice: defaultUnitPrice * factor,
                        currencySymbol: defaultSymbol,
                        currencyCode: defaultCurrencyCode,
                        nextBreakQty: null,
                        nextBreakPrice: null,
                    };
                }
            }
        }

        return {
            unitPrice: defaultUnitPrice,
            currencySymbol: defaultSymbol,
            currencyCode: defaultCurrencyCode,
            nextBreakQty: null,
            nextBreakPrice: null,
        };
    }

    if (product.quantity_breaks_enabled === true) {
        const breakRows = getBreakpointRowsForUom(product, normalizedUom);
        if (breakRows.length > 0) {
            let selectedRow = breakRows[0];
            let nextRow: BreakpointRow | null = null;
            for (const row of breakRows) {
                if (normalizedQty >= row.min_qty) {
                    selectedRow = row;
                    continue;
                }
                nextRow = row;
                break;
            }

            return {
                unitPrice: selectedRow.price,
                currencySymbol: normalizeCurrencySymbol(selectedRow.currency_symbol || defaultSymbol),
                currencyCode: String(selectedRow.currency_code || defaultCurrencyCode).trim().toUpperCase(),
                nextBreakQty: nextRow ? nextRow.min_qty : null,
                nextBreakPrice: nextRow ? nextRow.price : null,
            };
        }
    }

    const salesRows = getSalesRowsForUom(product, normalizedUom);
    if (salesRows.length > 0) {
        let selectedRow = salesRows[0];
        let nextRow: SalesPriceRow | null = null;
        for (const row of salesRows) {
            const threshold = product.quantity_breaks_enabled === true ? row.quantity : 1;
            if (normalizedQty >= threshold) {
                selectedRow = row;
                continue;
            }
            nextRow = row;
            break;
        }

        return {
            unitPrice: selectedRow.price,
            currencySymbol: normalizeCurrencySymbol(selectedRow.currency_symbol || defaultSymbol),
            currencyCode: String(selectedRow.currency_code || defaultCurrencyCode).trim().toUpperCase(),
            nextBreakQty: nextRow ? nextRow.quantity : null,
            nextBreakPrice: nextRow ? nextRow.price : null,
        };
    }

    if (allowConversion) {
        const baseUom = getProductBaseUom(product);
        if (normalizedUom !== baseUom) {
            const factor = getConversionFactor(product, normalizedUom, baseUom);
            if (factor !== null) {
                const baseSnapshot = getPricingSnapshotForUom(product, normalizedQty, baseUom, false);
                return {
                    unitPrice: baseSnapshot.unitPrice * factor,
                    currencySymbol: baseSnapshot.currencySymbol,
                    currencyCode: baseSnapshot.currencyCode,
                    nextBreakQty: baseSnapshot.nextBreakQty,
                    nextBreakPrice: baseSnapshot.nextBreakPrice !== null ? baseSnapshot.nextBreakPrice * factor : null,
                };
            }
        }
    }

    return {
        unitPrice: defaultUnitPrice,
        currencySymbol: defaultSymbol,
        currencyCode: defaultCurrencyCode,
        nextBreakQty: null,
        nextBreakPrice: null,
    };
};

const OPTIONAL_COLUMN_KEYS = ['sku', 'uom', 'rrp', 'price', 'stock', 'total'] as const;

const normalizeVisibleOptionalColumns = (value: unknown): Set<string> => {
    if (!Array.isArray(value)) {
        return new Set(OPTIONAL_COLUMN_KEYS);
    }

    const normalized = new Set<string>();
    value.forEach((entry) => {
        const key = String(entry || '').trim().toLowerCase();
        if (OPTIONAL_COLUMN_KEYS.includes(key as typeof OPTIONAL_COLUMN_KEYS[number])) {
            normalized.add(key);
        }
    });

    return normalized;
};

const ProductResultTable: React.FC<ProductResultTableProps> = ({
    searchResponse,
    onSort,
    sort,
    templateCsvUrl,
    onPageChange,
    detailMode = false,
    productDetailBasePath = '',
    showToolbar = true,
    embedded = false,
    emptyMessage = 'No products found. Please try a different search.',
    fallbackProductLinksOpenInNewTab = true,
    uiSettings = {},
    compactDesktop = false,
}) => {
    const location = useLocation();
    const resolvedUiSettings = {
        ...(searchResponse?.settings || {}),
        ...uiSettings,
    };
    const visibleOptionalColumns = normalizeVisibleOptionalColumns(resolvedUiSettings.visible_columns);
    const allowAddToCartPermission = searchResponse?.settings?.allow_add_to_cart !== false;
    const showPricePermission = searchResponse?.settings?.show_price !== false;
    const showStockPermission = searchResponse?.settings?.show_stock !== false;
    const currencyDisplayMode = searchResponse?.settings?.currency_display_mode ?? searchResponse?.settings?.show_currency_code;
    const showSkuColumn = visibleOptionalColumns.has('sku');
    const showUomColumn = visibleOptionalColumns.has('uom');
    const showRrpColumn = showPricePermission && visibleOptionalColumns.has('rrp');
    const showPriceColumn = showPricePermission && visibleOptionalColumns.has('price');
    const showStockColumn = showStockPermission && visibleOptionalColumns.has('stock');
    const showTotalColumn = showPricePermission && visibleOptionalColumns.has('total');
    const showAddAllButton = allowAddToCartPermission && resolvedUiSettings.show_add_all_button !== false;
    const showUploadCsv = allowAddToCartPermission && resolvedUiSettings.show_upload_csv !== false;
    const showProductImage = resolvedUiSettings.show_product_image !== false;
    const showProductName = resolvedUiSettings.show_product_name !== false;
    const showProductDescription = resolvedUiSettings.show_product_description !== false;
    const showRrpStrikethrough = resolvedUiSettings.show_rrp_strikethrough !== false;
    const hasProductTextContent = showProductName || showProductDescription;
    const productColumnMinWidth = hasProductTextContent
        ? (compactDesktop ? '170px' : '200px')
        : (showProductImage ? '90px' : '120px');
    const productColumnWidth = hasProductTextContent ? undefined : '1%';
    const skeletonColumnCount = 3 + [showSkuColumn, showUomColumn, showRrpColumn, showPriceColumn, showStockColumn, showTotalColumn].filter(Boolean).length;
    const skuColumnWidth = compactDesktop ? '88px' : '100px';
    const uomColumnWidth = compactDesktop ? '118px' : '130px';
    const rrpColumnWidth = compactDesktop ? '84px' : '100px';
    const priceColumnWidth = compactDesktop ? '148px' : '170px';
    const stockColumnWidth = compactDesktop ? '88px' : '100px';
    const quantityColumnWidth = compactDesktop ? '128px' : '150px';
    const totalColumnWidth = compactDesktop ? '96px' : '120px';
    const actionColumnWidth = compactDesktop ? '176px' : '220px';

    const getResolvedSearchResponseTargetKeys = (results: Product[] = []): string[] => {
        const initialTargets = results.flatMap((product) => sanitizeBackInStockTargetKeys(product.back_in_stock_target_keys));
        return resolveBackInStockTargetKeys(initialTargets);
    };
    const [cartQuantities, setCartQuantities] = useState<Map<number, number>>(new Map());
    const [selectedUoms, setSelectedUoms] = useState<Map<number, string>>(new Map());

    const renderRrpValue = (
        amount: number,
        currencyCode?: string,
        currencySymbol?: string,
        useStrikethrough: boolean = false,
    ) => {
        if (!(amount > 0)) {
            return <span style={{fontSize: '0.85rem'}}>-</span>;
        }

        const formattedRrp = formatMoneyWithCurrencyCode(
            amount,
            currencyCode,
            currencyDisplayMode,
            currencySymbol,
        );

        if (!useStrikethrough) {
            return <span style={{fontSize: '0.85rem'}}>{formattedRrp}</span>;
        }

        return (
            <small className="text-decoration-line-through" style={{fontSize: '0.85rem'}}>
                {formattedRrp}
            </small>
        );
    };
    const [selectedVariationAttributesByProduct, setSelectedVariationAttributesByProduct] = useState<Map<number, Record<string, string>>>(new Map());
    const [quantityNotices, setQuantityNotices] = useState<Map<number, string>>(new Map());
    const [isAddingToCart, setIsAddingToCart] = useState<Set<number>>(new Set());
    const [serialOptionsByProduct, setSerialOptionsByProduct] = useState<Map<number, SerialOption[]>>(new Map());
    const [selectedSerialByProduct, setSelectedSerialByProduct] = useState<Map<number, string>>(new Map());
    const [serialLoadingByProduct, setSerialLoadingByProduct] = useState<Set<number>>(new Set());
    const [serialErrorsByProduct, setSerialErrorsByProduct] = useState<Map<number, string>>(new Map());
    const [backInStockSubmittingTargets, setBackInStockSubmittingTargets] = useState<Set<string>>(new Set());
    const [subscribedBackInStockTargets, setSubscribedBackInStockTargets] = useState<Set<string>>(() => {
        return new Set(getResolvedSearchResponseTargetKeys(searchResponse?.results || []));
    });
    const [draftSelectorState, setDraftSelectorState] = useState<{ productId: number; payload: DraftOrderItemInput } | null>(null);
    const [quoteSelectorState, setQuoteSelectorState] = useState<{ productId: number; payload: QuoteItemInput } | null>(null);
    const canCreateDraftOrders = searchResponse?.settings?.can_create_drafts === true;
    const canEditDraftItems = searchResponse?.settings?.can_edit_draft_items === true;
    const canShowDraftOrderButton = searchResponse?.settings?.draft_orders_enabled === true
        && (canCreateDraftOrders || canEditDraftItems);
    const canCreateQuotes = searchResponse?.settings?.can_create_quotes === true;
    const canEditQuotes = searchResponse?.settings?.can_edit_quotes === true;
    const canShowQuoteButton = searchResponse?.settings?.quote_management_enabled === true
        && (canCreateQuotes || canEditQuotes);
    const backInStockEnabled = searchResponse?.settings?.back_in_stock_b2b_enabled === true;
    const backInStockButtonLabel = String(searchResponse?.settings?.back_in_stock_button_label || '').trim() || 'Notify when back in stock';
    const backInStockSuccessMessage = String(searchResponse?.settings?.back_in_stock_success_message || '').trim() || 'We will email you when this item is back in stock.';
    const backInStockRemoveButtonLabel = 'Remove back in stock notification';
    const backInStockRemoveMessage = 'Back in stock notification removed.';
    const customerWarehouseId = String(searchResponse?.settings?.customer_warehouse_id || '').trim();
    const warehouseSummaryRowDisplay = showStockPermission
        ? String(searchResponse?.settings?.warehouse_summary_row_display || 'off').trim()
        : 'off';
    const backInStockApiEndpoint = buildApiEndpoint('back-in-stock/subscriptions');
    const showQuantityColumn = allowAddToCartPermission || canShowDraftOrderButton || canShowQuoteButton;
    const showActionColumn = allowAddToCartPermission || canShowDraftOrderButton || canShowQuoteButton || (showStockPermission && backInStockEnabled);
    const sortSelectValue = !showPricePermission && (sort === 'price_low_to_high' || sort === 'price_high_to_low')
        ? ''
        : sort;

    useEffect(() => {
        setSubscribedBackInStockTargets(new Set(getResolvedSearchResponseTargetKeys(searchResponse?.results || [])));
    }, [searchResponse?.results]);

    useEffect(() => {
        return onBackInStockTargetStateChange(() => {
            setSubscribedBackInStockTargets(new Set(getResolvedSearchResponseTargetKeys(searchResponse?.results || [])));
        });
    }, [searchResponse?.results]);

    const getProductById = (productId: number) => {
        return searchResponse?.results?.find((product) => product.id === productId);
    };

    const getVariationStateForProduct = (
        product: Product,
        overrideSelection?: Record<string, string>
    ): VariationSelectionResult => {
        const selection = overrideSelection ?? selectedVariationAttributesByProduct.get(product.id);
        return resolveVariationSelection(product, selection);
    };

    const getActiveProductForProduct = (
        product: Product,
        variationState?: VariationSelectionResult
    ): Product => {
        const resolvedVariationState = variationState ?? getVariationStateForProduct(product);
        return getActiveProductForVariation(product, resolvedVariationState.selectedVariation);
    };

    const getSelectedUomForProduct = (product: Product, activeProduct?: Product): string => {
        const resolvedProduct = activeProduct || product;
        const selectedUom = normalizeUom(selectedUoms.get(product.id));
        const availableUoms = getAvailableUoms(resolvedProduct);
        if (selectedUom !== '' && availableUoms.includes(selectedUom)) {
            return selectedUom;
        }

        return getDefaultUomForProduct(resolvedProduct);
    };

    const getSerialOptionsForProduct = (product: Product): SerialOption[] => {
        return serialOptionsByProduct.get(product.id) || [];
    };

    const getSelectedSerialForProduct = (product: Product): SerialOption | null => {
        const selectedKey = selectedSerialByProduct.get(product.id) || '';
        if (selectedKey === '') {
            return null;
        }

        const serialOptions = getSerialOptionsForProduct(product);
        return serialOptions.find((option) => getSerialOptionKey(option) === selectedKey) || null;
    };

    const getPolicyForSelection = (activeProduct: Product, selectedUom: string, selectedSerial: SerialOption | null): InventoryPolicy => {
        const basePolicy = resolveInventoryPolicyForUom(activeProduct, selectedUom);

        if (!selectedSerial || isSerialForceBuyAll(activeProduct)) {
            return basePolicy;
        }

        const serialQuantity = Math.max(0, Number(selectedSerial.quantity || 0));
        const policyMax = getPolicyMax(basePolicy);
        const resolvedMax = policyMax === null ? serialQuantity : Math.min(policyMax, serialQuantity);

        return {
            ...basePolicy,
            max_qty: resolvedMax,
        };
    };

    const getEffectivePolicyForProduct = (product: Product, activeProduct: Product, selectedUom: string): InventoryPolicy => {
        return getPolicyForSelection(activeProduct, selectedUom, getSelectedSerialForProduct(product));
    };

    const getSerialEndpointForProduct = (
        product: Product,
        activeProduct: Product,
        variationState: VariationSelectionResult
    ): string => {
        if (!isSerialEnabled(activeProduct)) {
            return '';
        }

        if (variationState.isVariableProduct && !variationState.isSelectionValid) {
            return '';
        }

        const endpointSeed = String(activeProduct.serial_options_endpoint || '').trim();
        if (endpointSeed === '') {
            return '';
        }

        const targetProductId = variationState.selectedVariation?.id || product.id;
        return buildSerialOptionsEndpointForProduct(endpointSeed, targetProductId);
    };

    const setQuantityForProduct = (
        product: Product,
        activeProduct: Product,
        quantity: number,
        showNotice: boolean = false
    ): QuantityClampResult => {
        const selectedUom = getSelectedUomForProduct(product, activeProduct);
        const policy = getEffectivePolicyForProduct(product, activeProduct, selectedUom);
        const selectedSerial = getSelectedSerialForProduct(product);

        let quantityResult: QuantityClampResult;
        if (isSerialEnabled(activeProduct) && isSerialForceBuyAll(activeProduct) && selectedSerial) {
            const forcedQuantity = Number(selectedSerial.quantity || 0);
            const safeForcedQuantity = Number.isFinite(forcedQuantity) ? Math.max(0, forcedQuantity) : 0;
            quantityResult = {
                quantity: safeForcedQuantity,
                adjusted: Math.abs(safeForcedQuantity - quantity) > 0.000001,
                reason: 'serial_force_buy_all',
            };
        } else {
            quantityResult = clampQuantityToPolicy(quantity, policy);
        }

        setCartQuantities((prevState) => {
            const newState = new Map(prevState);
            newState.set(product.id, Math.max(0, quantityResult.quantity));
            return newState;
        });

        setQuantityNotices((prevState) => {
            const newState = new Map(prevState);
            if (showNotice && quantityResult.reason === 'serial_force_buy_all' && selectedSerial) {
                newState.set(product.id, `Force buy all enabled. Quantity locked to ${formatQuantity(selectedSerial.quantity, true)}.`);
            } else if (showNotice && quantityResult.adjusted) {
                newState.set(product.id, buildPolicyMessage(quantityResult, policy, selectedUom || 'EA', showStockPermission));
            } else {
                newState.delete(product.id);
            }
            return newState;
        });

        return quantityResult;
    };

    const handleSortFilterChange = (event: ChangeEvent<HTMLSelectElement>) => {
        onSort(event.target.value);
    };

    useEffect(() => {
        const nextQuantities = new Map<number, number>();
        const nextUoms = new Map<number, string>();
        const nextVariationSelections = new Map<number, Record<string, string>>();

        (searchResponse?.results || []).forEach((product) => {
            const variationAttributes = getVariationAttributesForProduct(product);
            const defaultSelection = getDefaultVariationSelection(product, variationAttributes);
            if (Object.keys(defaultSelection).length > 0) {
                nextVariationSelections.set(product.id, defaultSelection);
            }

            const variationState = resolveVariationSelection(product, defaultSelection);
            const activeProduct = getActiveProductForVariation(product, variationState.selectedVariation);
            const defaultUom = getDefaultUomForProduct(activeProduct);
            nextUoms.set(product.id, defaultUom);

            const policy = resolveInventoryPolicyForUom(activeProduct, defaultUom);
            const quantityResult = clampQuantityToPolicy(0, policy);
            nextQuantities.set(product.id, quantityResult.quantity);
        });

        setSelectedVariationAttributesByProduct(nextVariationSelections);
        setCartQuantities(nextQuantities);
        setSelectedUoms(nextUoms);
        setQuantityNotices(new Map());
        setSerialOptionsByProduct(new Map());
        setSelectedSerialByProduct(new Map());
        setSerialLoadingByProduct(new Set());
        setSerialErrorsByProduct(new Map());
    }, [searchResponse]);

    useEffect(() => {
        const products = searchResponse?.results || [];
        if (products.length === 0) {
            setSerialLoadingByProduct(new Set());
            return;
        }

        const fetchTargets: Array<{productId: number; endpoint: string}> = [];
        const blockedErrors = new Map<number, string>();
        const abortController = new AbortController();

        products.forEach((product) => {
            const variationState = getVariationStateForProduct(product);
            const activeProduct = getActiveProductForProduct(product, variationState);
            const serialEnabled = isSerialEnabled(activeProduct);
            if (!serialEnabled) {
                setSerialOptionsByProduct((prevState) => {
                    const nextState = new Map(prevState);
                    nextState.delete(product.id);
                    return nextState;
                });
                setSelectedSerialByProduct((prevState) => {
                    const nextState = new Map(prevState);
                    nextState.delete(product.id);
                    return nextState;
                });
                return;
            }

            if (variationState.isVariableProduct && !variationState.isSelectionValid) {
                blockedErrors.set(product.id, variationState.selectionMessage || 'Select product options to load serial stock.');
                setSerialOptionsByProduct((prevState) => {
                    const nextState = new Map(prevState);
                    nextState.set(product.id, []);
                    return nextState;
                });
                setSelectedSerialByProduct((prevState) => {
                    const nextState = new Map(prevState);
                    nextState.delete(product.id);
                    return nextState;
                });
                return;
            }

            const endpoint = getSerialEndpointForProduct(product, activeProduct, variationState);
            if (endpoint === '') {
                blockedErrors.set(product.id, 'Serial options endpoint is not configured.');
                setSerialOptionsByProduct((prevState) => {
                    const nextState = new Map(prevState);
                    nextState.set(product.id, []);
                    return nextState;
                });
                setSelectedSerialByProduct((prevState) => {
                    const nextState = new Map(prevState);
                    nextState.delete(product.id);
                    return nextState;
                });
                return;
            }

            fetchTargets.push({productId: product.id, endpoint});
        });

        setSerialErrorsByProduct((prevState) => {
            const nextState = new Map(prevState);
            products.forEach((product) => {
                if (blockedErrors.has(product.id)) {
                    nextState.set(product.id, blockedErrors.get(product.id) || '');
                } else {
                    nextState.delete(product.id);
                }
            });
            return nextState;
        });

        if (fetchTargets.length === 0) {
            setSerialLoadingByProduct(new Set());
            return () => {
                abortController.abort();
            };
        }

        setSerialLoadingByProduct(new Set(fetchTargets.map((row) => row.productId)));

        fetchTargets.forEach(({productId, endpoint}) => {
            void fetch(endpoint, {
                credentials: 'include',
                signal: abortController.signal,
                headers: {
                    Accept: 'application/json',
                },
            }).then(async (response) => {
                const data = await response.json();
                if (!response.ok) {
                    throw new Error(String(data?.message || data?.error || 'Failed to load serial options.'));
                }

                const options = Array.isArray(data?.options)
                    ? data.options
                        .map((option: any) => normalizeSerialOption(option))
                        .filter((option: SerialOption | null): option is SerialOption => option !== null && option.quantity > 0)
                    : [];

                setSerialOptionsByProduct((prevState) => {
                    const nextState = new Map(prevState);
                    nextState.set(productId, options);
                    return nextState;
                });
                setSelectedSerialByProduct((prevState) => {
                    const nextState = new Map(prevState);
                    const selectedKey = nextState.get(productId);
                    if (selectedKey && !options.some((option) => getSerialOptionKey(option) === selectedKey)) {
                        nextState.delete(productId);
                    }
                    return nextState;
                });
                setSerialErrorsByProduct((prevState) => {
                    const nextState = new Map(prevState);
                    nextState.delete(productId);
                    return nextState;
                });
            }).catch((error) => {
                if (abortController.signal.aborted) {
                    return;
                }

                const message = error instanceof Error ? error.message : 'Failed to load serial options.';
                setSerialOptionsByProduct((prevState) => {
                    const nextState = new Map(prevState);
                    nextState.set(productId, []);
                    return nextState;
                });
                setSelectedSerialByProduct((prevState) => {
                    const nextState = new Map(prevState);
                    nextState.delete(productId);
                    return nextState;
                });
                setSerialErrorsByProduct((prevState) => {
                    const nextState = new Map(prevState);
                    nextState.set(productId, message);
                    return nextState;
                });
            }).finally(() => {
                if (abortController.signal.aborted) {
                    return;
                }

                setSerialLoadingByProduct((prevState) => {
                    const nextState = new Set(prevState);
                    nextState.delete(productId);
                    return nextState;
                });
            });
        });

        return () => {
            abortController.abort();
        };
    }, [searchResponse, selectedVariationAttributesByProduct]);

    const onVariationAttributeChange = (product: Product, attributeKey: string, attributeValue: string) => {
        const currentSelection = selectedVariationAttributesByProduct.get(product.id) || {};
        const nextSelection = {
            ...currentSelection,
            [attributeKey]: normalizeVariationValue(attributeValue),
        };

        setSelectedVariationAttributesByProduct((prevState) => {
            const newState = new Map(prevState);
            newState.set(product.id, nextSelection);
            return newState;
        });

        setSelectedSerialByProduct((prevState) => {
            const nextState = new Map(prevState);
            nextState.delete(product.id);
            return nextState;
        });
        setSerialOptionsByProduct((prevState) => {
            const nextState = new Map(prevState);
            nextState.delete(product.id);
            return nextState;
        });
        setSerialErrorsByProduct((prevState) => {
            const nextState = new Map(prevState);
            nextState.delete(product.id);
            return nextState;
        });

        const variationState = resolveVariationSelection(product, nextSelection);
        const activeProduct = getActiveProductForVariation(product, variationState.selectedVariation);
        const availableUoms = getAvailableUoms(activeProduct);
        const currentUom = normalizeUom(selectedUoms.get(product.id));
        const nextUom = currentUom !== '' && availableUoms.includes(currentUom)
            ? currentUom
            : getDefaultUomForProduct(activeProduct);

        setSelectedUoms((prevState) => {
            const nextState = new Map(prevState);
            nextState.set(product.id, nextUom);
            return nextState;
        });

        const currentQty = cartQuantities.get(product.id) ?? 0;
        const policy = resolveInventoryPolicyForUom(activeProduct, nextUom);
        const quantityResult = clampQuantityToPolicy(currentQty, policy);
        setCartQuantities((prevState) => {
            const nextState = new Map(prevState);
            nextState.set(product.id, quantityResult.quantity);
            return nextState;
        });
        setQuantityNotices((prevState) => {
            const nextState = new Map(prevState);
            if (!variationState.isSelectionValid && variationState.selectionMessage !== '') {
                nextState.set(product.id, variationState.selectionMessage);
            } else if (quantityResult.adjusted) {
                nextState.set(product.id, buildPolicyMessage(quantityResult, policy, nextUom || 'EA', showStockPermission));
            } else {
                nextState.delete(product.id);
            }
            return nextState;
        });
    };

    const onQuantityChange = (event: ChangeEvent<HTMLInputElement>, product: Product) => {
        const variationState = getVariationStateForProduct(product);
        const activeProduct = getActiveProductForProduct(product, variationState);
        if (isSerialEnabled(activeProduct) && isSerialForceBuyAll(activeProduct) && getSelectedSerialForProduct(product)) {
            return;
        }

        const rawValue = String(event.target.value || '').trim();
        if (rawValue === '') {
            setQuantityForProduct(product, activeProduct, 0, false);
            return;
        }

        const parsedQty = Number(rawValue.replace(/,/g, ''));
        if (!Number.isFinite(parsedQty)) {
            return;
        }

        setQuantityForProduct(product, activeProduct, parsedQty, true);
    };

    const onUomChange = (product: Product, nextUom: string) => {
        const variationState = getVariationStateForProduct(product);
        const activeProduct = getActiveProductForProduct(product, variationState);
        const normalizedUom = normalizeUom(nextUom) || 'EA';
        setSelectedUoms((prevState) => {
            const newState = new Map(prevState);
            newState.set(product.id, normalizedUom);
            return newState;
        });

        const selectedSerial = getSelectedSerialForProduct(product);
        if (isSerialEnabled(activeProduct) && isSerialForceBuyAll(activeProduct) && selectedSerial) {
            setQuantityForProduct(product, activeProduct, Number(selectedSerial.quantity || 0), false);
            return;
        }

        const currentQty = cartQuantities.get(product.id) ?? 0;
        const policy = getEffectivePolicyForProduct(product, activeProduct, normalizedUom);
        const quantityResult = clampQuantityToPolicy(currentQty, policy);
        setCartQuantities((prevState) => {
            const newState = new Map(prevState);
            newState.set(product.id, quantityResult.quantity);
            return newState;
        });
        setQuantityNotices((prevState) => {
            const newState = new Map(prevState);
            if (quantityResult.adjusted) {
                newState.set(product.id, buildPolicyMessage(quantityResult, policy, normalizedUom, showStockPermission));
            } else {
                newState.delete(product.id);
            }
            return newState;
        });
    };

    const onSerialChange = (product: Product, nextSerialKey: string) => {
        const variationState = getVariationStateForProduct(product);
        const activeProduct = getActiveProductForProduct(product, variationState);
        const selectedSerial = nextSerialKey === ''
            ? null
            : (getSerialOptionsForProduct(product).find((option) => getSerialOptionKey(option) === nextSerialKey) || null);

        setSelectedSerialByProduct((prevState) => {
            const newState = new Map(prevState);
            if (nextSerialKey === '') {
                newState.delete(product.id);
            } else {
                newState.set(product.id, nextSerialKey);
            }
            return newState;
        });

        const selectedUom = getSelectedUomForProduct(product, activeProduct);
        const currentQty = cartQuantities.get(product.id) ?? 0;

        if (isSerialEnabled(activeProduct) && isSerialForceBuyAll(activeProduct) && selectedSerial) {
            const forcedQuantity = Number(selectedSerial.quantity || 0);
            const safeForcedQuantity = Number.isFinite(forcedQuantity) ? Math.max(0, forcedQuantity) : 0;

            setCartQuantities((prevState) => {
                const newState = new Map(prevState);
                newState.set(product.id, safeForcedQuantity);
                return newState;
            });
            setQuantityNotices((prevState) => {
                const newState = new Map(prevState);
                newState.set(product.id, `Force buy all enabled. Quantity locked to ${formatQuantity(safeForcedQuantity, true)}.`);
                return newState;
            });
            return;
        }

        const policy = getPolicyForSelection(activeProduct, selectedUom, selectedSerial);
        const quantityResult = clampQuantityToPolicy(currentQty, policy);
        setCartQuantities((prevState) => {
            const newState = new Map(prevState);
            newState.set(product.id, quantityResult.quantity);
            return newState;
        });
        setQuantityNotices((prevState) => {
            const newState = new Map(prevState);
            if (quantityResult.adjusted) {
                newState.set(product.id, buildPolicyMessage(quantityResult, policy, selectedUom || 'EA', showStockPermission));
            } else {
                newState.delete(product.id);
            }
            return newState;
        });
    };

    async function addToCart(productId: number, quantity: number) {
        const product = getProductById(productId);
        if (!product || !productId) {
            toast.error('Please select a quantity');
            return;
        }

        const variationState = getVariationStateForProduct(product);
        if (variationState.isVariableProduct && !variationState.isSelectionValid) {
            toast.error(variationState.selectionMessage || 'Please select product options.');
            return;
        }

        const activeProduct = getActiveProductForProduct(product, variationState);
        const selectedUom = getSelectedUomForProduct(product, activeProduct);
        const policy = getEffectivePolicyForProduct(product, activeProduct, selectedUom);
        const quantityResult = clampQuantityToPolicy(quantity, policy);
        let effectiveQuantity = quantityResult.quantity;
        if (quantityResult.adjusted) {
            setQuantityForProduct(product, activeProduct, effectiveQuantity, true);
        }

        const selectedSerial = getSelectedSerialForProduct(product);
        if (isSerialEnabled(activeProduct)) {
            if (selectedSerial && isSerialForceBuyAll(activeProduct)) {
                const forcedQuantity = Number(selectedSerial.quantity || 0);
                const safeForcedQuantity = Number.isFinite(forcedQuantity) ? Math.max(0, forcedQuantity) : 0;
                if (Math.abs(safeForcedQuantity - effectiveQuantity) > 0.000001) {
                    effectiveQuantity = safeForcedQuantity;
                    setQuantityForProduct(product, activeProduct, safeForcedQuantity, false);
                }
            }
        }

        if (!effectiveQuantity || effectiveQuantity < 1) {
            toast.error('Please select a quantity');
            return;
        }
        if (!canAddToCartForProduct(activeProduct) || policy.can_purchase === false) {
            toast.error('This product is not currently available for purchase.');
            return;
        }

        // Prevent duplicate requests
        if (isAddingToCart.has(productId)) {
            return;
        }

        if (!allowAddToCartPermission) {
            toast.error('Add to cart is disabled for your role on this commerce surface.');
            return;
        }

        const payload: Record<string, any> = {product_id: productId, quantity: effectiveQuantity, surface: 'archive_add_to_cart'};
        if (activeProduct.uom_enabled === true && selectedUom !== '') {
            payload.uom = selectedUom;
        }
        if (customerWarehouseId !== '') {
            payload.warehouse_id = customerWarehouseId;
        }
        if (selectedSerial) {
            payload.serial_number = selectedSerial.serial_number;
            payload.serial_warehouse_id = selectedSerial.warehouse_id || '';
            payload.serial_location_id = selectedSerial.location_id || '';
        }
        if (variationState.selectedVariation && variationState.selectedVariation.id > 0) {
            payload.variation_id = variationState.selectedVariation.id;
            payload.variation = variationState.selectedPayload;
            Object.entries(variationState.selectedPayload).forEach(([attributeKey, attributeValue]) => {
                payload[attributeKey] = attributeValue;
            });
        }

        setIsAddingToCart((prev) => new Set(prev).add(productId));

        try {
            const response = await fetch('/wp-json/weka/v1/cart/add-item', {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': wekaApiSettings.nonce,
                },
                body: JSON.stringify(payload),
            });

            const data = await response.json();

            if (!response.ok) {
                const errorMessage = data.message || data.error || 'Failed to add to cart';
                console.error('Add to cart error:', data);
                throw new Error(errorMessage);
            }

            if (!data.success) {
                throw new Error(data.message || 'Failed to add to cart');
            }

            applyWooCartUpdates(data);

            resetProductSelectionAfterSuccess(product.id);

            toast.success(`Added ${formatQuantity(effectiveQuantity, policy.is_decimal_allowed === true)} x ${product.name}`);
        } catch (error) {
            console.error('Add to cart error:', error);
            const errorMessage = error instanceof Error ? error.message : 'Failed to add item to cart';
            toast.error(errorMessage);
        } finally {
            setIsAddingToCart((prev) => {
                const newSet = new Set(prev);
                newSet.delete(productId);
                return newSet;
            });
        }
    }

    async function subscribeToBackInStock(productId: number) {
        const product = getProductById(productId);
        if (!product || !productId) {
            toast.error('Unable to track stock for this product.');
            return;
        }

        const variationState = getVariationStateForProduct(product);
        const activeProduct = getActiveProductForProduct(product, variationState);
        const selectedUom = getSelectedUomForProduct(product, activeProduct);
        const inventoryPolicy = getEffectivePolicyForProduct(product, activeProduct, selectedUom);
        const targetVariationId = variationState.selectedVariation?.id || 0;
        const targetKey = buildBackInStockTargetKey(product.id, targetVariationId);

        if (targetKey === '') {
            toast.error('Unable to track stock for this product.');
            return;
        }

        if (
            variationState.isVariableProduct
            && (!variationState.isSelectionComplete || targetVariationId <= 0 || variationState.selectedVariation === null)
        ) {
            toast.error(variationState.selectionMessage || 'Please select product options first.');
            return;
        }

        if (!shouldOfferBackInStockForProduct({
            featureEnabled: backInStockEnabled,
            variationState,
            activeProduct,
            inventoryPolicy,
        })) {
            toast.error('This product is not currently eligible for stock reminders.');
            return;
        }

        if (backInStockSubmittingTargets.has(targetKey)) {
            return;
        }

        const isSubscribed = subscribedBackInStockTargets.has(targetKey);

        setBackInStockSubmittingTargets((prevTargets) => {
            const nextTargets = new Set(prevTargets);
            nextTargets.add(targetKey);
            return nextTargets;
        });

        try {
            const response = await fetch(backInStockApiEndpoint, {
                method: isSubscribed ? 'DELETE' : 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': (window as any)?.wekaApiSettings?.nonce || '',
                },
                body: JSON.stringify({
                    product_id: product.id,
                    variation_id: targetVariationId > 0 ? targetVariationId : undefined,
                    variation: !isSubscribed && targetVariationId > 0 ? variationState.selectedPayload : undefined,
                    source_surface: !isSubscribed ? 'b2b_products_row' : undefined,
                }),
            });

            let data: any = {};
            try {
                data = await response.json();
            } catch {
                data = {};
            }

            if (!response.ok || data?.success !== true) {
                throw new Error(String(data?.message || data?.error || (isSubscribed
                    ? 'Failed to remove the stock reminder.'
                    : 'Failed to save the stock reminder.')));
            }

            setSubscribedBackInStockTargets((prevTargets) => {
                const nextTargets = new Set(prevTargets);
                if (isSubscribed) {
                    nextTargets.delete(targetKey);
                } else {
                    nextTargets.add(targetKey);
                }
                return nextTargets;
            });
            setBackInStockTargetSubscriptionState(targetKey, !isSubscribed);
            toast.success(String(data?.message || '').trim() || (isSubscribed ? backInStockRemoveMessage : backInStockSuccessMessage));
        } catch (error) {
            const message = error instanceof Error
                ? error.message
                : (isSubscribed ? 'Failed to remove the stock reminder.' : 'Failed to save the stock reminder.');
            toast.error(message);
        } finally {
            setBackInStockSubmittingTargets((prevTargets) => {
                const nextTargets = new Set(prevTargets);
                nextTargets.delete(targetKey);
                return nextTargets;
            });
        }
    }

    const buildSavedItemPayloadContextForProduct = (
        productId: number,
        destinationLabel: string
    ): SavedItemPayloadContext | null => {
        const product = getProductById(productId);
        if (!product || !productId) {
            toast.error(`Unable to add this item to ${destinationLabel}.`);
            return null;
        }

        const variationState = getVariationStateForProduct(product);
        if (variationState.isVariableProduct && !variationState.isSelectionValid) {
            toast.error(variationState.selectionMessage || `Please select product options before adding to ${destinationLabel}.`);
            return null;
        }

        const activeProduct = getActiveProductForProduct(product, variationState);
        const selectedUom = getSelectedUomForProduct(product, activeProduct);
        const policy = getEffectivePolicyForProduct(product, activeProduct, selectedUom);
        const selectedSerial = getSelectedSerialForProduct(product);
        const currentQuantity = cartQuantities.get(product.id) ?? 0;
        const quantityResult = clampQuantityToPolicy(currentQuantity, policy);
        let effectiveQuantity = quantityResult.quantity;

        if (quantityResult.adjusted) {
            setQuantityForProduct(product, activeProduct, effectiveQuantity, true);
        }

        if (isSerialEnabled(activeProduct) && selectedSerial && isSerialForceBuyAll(activeProduct)) {
            const forcedQuantity = Number(selectedSerial.quantity || 0);
            const safeForcedQuantity = Number.isFinite(forcedQuantity) ? Math.max(0, forcedQuantity) : 0;
            if (Math.abs(safeForcedQuantity - effectiveQuantity) > 0.000001) {
                effectiveQuantity = safeForcedQuantity;
                setQuantityForProduct(product, activeProduct, safeForcedQuantity, false);
            }
        }

        if (!effectiveQuantity || effectiveQuantity < 1) {
            toast.error(`Please select a quantity before adding to ${destinationLabel}.`);
            return null;
        }

        if (!canAddToCartForProduct(activeProduct) || policy.can_purchase === false) {
            toast.error('This product is not currently available for purchase.');
            return null;
        }

        const payload: DraftOrderItemInput = {
            product_id: productId,
            quantity: effectiveQuantity,
        };

        if (activeProduct.uom_enabled === true && selectedUom !== '') {
            payload.uom = selectedUom;
        }
        if (customerWarehouseId !== '') {
            payload.warehouse_id = customerWarehouseId;
        }
        if (selectedSerial) {
            payload.serial_number = selectedSerial.serial_number;
            payload.serial_warehouse_id = selectedSerial.warehouse_id || '';
            payload.serial_location_id = selectedSerial.location_id || '';
        }
        if (variationState.selectedVariation && variationState.selectedVariation.id > 0) {
            payload.variation_id = variationState.selectedVariation.id;
            payload.variation = variationState.selectedPayload;
        }

        const pricingSnapshot = getPricingSnapshotForUom(activeProduct, Math.max(1, effectiveQuantity), selectedUom);

        return {
            payload,
            unitPrice: Number.isFinite(pricingSnapshot.unitPrice) ? pricingSnapshot.unitPrice : 0,
        };
    };

    const buildDraftPayloadForProduct = (productId: number): DraftOrderItemInput | null => {
        const context = buildSavedItemPayloadContextForProduct(productId, 'a draft order');
        return context ? context.payload : null;
    };

    const buildQuotePayloadForProduct = (productId: number): QuoteItemInput | null => {
        const context = buildSavedItemPayloadContextForProduct(productId, 'a quote');
        if (!context) {
            return null;
        }

        return {
            ...context.payload,
            price: context.unitPrice,
            discount: 0,
        };
    };

    const openDraftSelector = (productId: number) => {
        const payload = buildDraftPayloadForProduct(productId);
        if (!payload) {
            return;
        }

        setDraftSelectorState({
            productId,
            payload,
        });
    };

    const openQuoteSelector = (productId: number) => {
        const payload = buildQuotePayloadForProduct(productId);
        if (!payload) {
            return;
        }

        setQuoteSelectorState({
            productId,
            payload,
        });
    };

    const resetProductSelectionAfterSuccess = (productId: number) => {
        const product = getProductById(productId);
        if (!product) {
            return;
        }

        const variationState = getVariationStateForProduct(product);
        const activeProduct = getActiveProductForProduct(product, variationState);
        const shouldClearSerialSelection = isSerialEnabled(activeProduct)
            && isSerialForceBuyAll(activeProduct)
            && getSelectedSerialForProduct(product) !== null;

        if (shouldClearSerialSelection) {
            setSelectedSerialByProduct((prevState) => {
                const nextState = new Map(prevState);
                nextState.delete(product.id);
                return nextState;
            });
        }

        setCartQuantities((prevState) => {
            const nextState = new Map(prevState);
            nextState.set(product.id, 0);
            return nextState;
        });
        setQuantityNotices((prevState) => {
            const nextState = new Map(prevState);
            nextState.delete(product.id);
            return nextState;
        });
    };

    const addAllToCart = () => {
        if (!allowAddToCartPermission) {
            return;
        }

        cartQuantities.forEach((value, key) => {
            if (value > 0) {
                const product = getProductById(key);
                if (!product) {
                    return;
                }
                const variationState = getVariationStateForProduct(product);
                if (variationState.isVariableProduct && !variationState.isSelectionValid) {
                    return;
                }
                addToCart(key, value);
            }
        });
    };

    const normalizedProductDetailBasePath = (() => {
        const raw = String(productDetailBasePath || '').trim();
        if (raw === '') {
            return '';
        }

        const noLeadingSlash = raw.replace(/^\/+/, '');
        const noTrailingSlash = noLeadingSlash.replace(/\/+$/, '');
        if (noTrailingSlash === '') {
            return '';
        }

        return `/${noTrailingSlash}`;
    })();

    const currentReturnPath = (() => {
        const pathname = String(location.pathname || '').trim();
        const safePath = pathname.startsWith('/find-products') ? pathname : '/find-products';
        return `${safePath}${String(location.search || '')}`;
    })();

    const buildProductDetailRoute = (product: Product): string => {
        const productId = Number(product.id || 0);
        if (detailMode || normalizedProductDetailBasePath === '' || productId <= 0) {
            return '';
        }

        const slug = String(product.slug || '').trim();
        const identifier = slug !== '' ? slug : String(productId);
        if (identifier === '') {
            return '';
        }

        const query = new URLSearchParams();
        query.set('return', currentReturnPath);
        return `${normalizedProductDetailBasePath}/${encodeURIComponent(identifier)}?${query.toString()}`;
    };

    const getProductRowState = (product: Product) => {
        const qty = cartQuantities.get(product.id) ?? 0;
        const variationState = getVariationStateForProduct(product);
        const activeProduct = getActiveProductForProduct(product, variationState);
        const selectedUom = getSelectedUomForProduct(product, activeProduct);
        const variationAttributes = variationState.variationAttributes;
        const selectedVariationAttributes = variationState.selectedAttributes;
        const variationSelectionMessage = variationState.selectionMessage;
        const serialOptions = getSerialOptionsForProduct(product);
        const selectedSerial = getSelectedSerialForProduct(product);
        const selectedSerialKey = selectedSerialByProduct.get(product.id) || '';
        const serialLoading = serialLoadingByProduct.has(product.id);
        const serialError = serialErrorsByProduct.get(product.id) || '';
        const serialEnabledForProduct = isSerialEnabled(activeProduct);
        const serialForceBuyAll = isSerialForceBuyAll(activeProduct);
        const serialDisplayMode = getSerialDisplayMode(activeProduct);
        const serialQuantityLocked = serialEnabledForProduct && serialForceBuyAll && selectedSerial !== null;
        const effectiveQty = serialQuantityLocked && selectedSerial
            ? Math.max(0, Number(selectedSerial.quantity || 0))
            : qty;
        const inventoryPolicy = getEffectivePolicyForProduct(product, activeProduct, selectedUom);
        const policyStep = getPolicyStep(inventoryPolicy);
        const policyMax = getPolicyMax(inventoryPolicy);
        const policyAllowZero = inventoryPolicy.allow_zero === true;
        const policyMinNonZero = getPolicyMinNonZero(inventoryPolicy);
        const decimalAllowed = inventoryPolicy.is_decimal_allowed === true;
        const unitDisplayQty = Math.max(1, effectiveQty);
        const pricingSnapshot = getPricingSnapshotForUom(activeProduct, unitDisplayQty, selectedUom);
        const unitPrice = pricingSnapshot.unitPrice;
        const currencySymbol = normalizeCurrencySymbol(pricingSnapshot.currencySymbol || activeProduct.currency_symbol);
        const currencyCode = String(
            pricingSnapshot.currencyCode
            || activeProduct.currencyCode
            || product.currency_code
            || searchResponse?.settings?.default_currency_code
            || ''
        ).trim().toUpperCase();
        const totalPrice = effectiveQty > 0 ? (unitPrice * effectiveQty) : null;
        const pricingUnavailableMessage = String(
            variationState.selectedVariation?.pricingUnavailableMessage
            || product.pricing_unavailable_message
            || ''
        ).trim();
        const isAdding = isAddingToCart.has(product.id);
        const quantityNotice = quantityNotices.get(product.id) || '';
        const availableUoms = getAvailableUoms(activeProduct);
        const showUomSelector = activeProduct.uom_enabled === true && availableUoms.length > 1;
        const rrpValue = Number(activeProduct.rrp || 0);
        const canPurchaseProduct = variationState.isSelectionValid
            && canAddToCartForProduct(activeProduct)
            && inventoryPolicy.can_purchase !== false;
        const showEnquiryButton = allowAddToCartPermission && product.is_enquiry_product === true;
        const enquiryButtonText = String(product.enquiry_button_text || 'Enquire Now').trim() || 'Enquire Now';
        const showBackorder = showStockPermission
            && canPurchaseProduct
            && (
                inventoryPolicy.normalized_status === 'backorder'
                || inventoryPolicy.backorders_allowed === true
                || activeProduct.backorders_allowed === true
                || activeProduct.is_on_backorder === true
            )
            && (activeProduct.stock === null || activeProduct.stock <= 0);
        const showBackorderLabelOnAddToCart = showStockPermission
            && searchResponse?.settings?.show_backorder_label_on_add_to_cart === true
            && inventoryPolicy.backorders_require_notification === true
            && showBackorder;
        const backInStockTargetKey = buildBackInStockTargetKey(product.id, variationState.selectedVariation?.id || 0);
        const isBackInStockSubmitting = backInStockSubmittingTargets.has(backInStockTargetKey);
        const isBackInStockSubscribed = subscribedBackInStockTargets.has(backInStockTargetKey);
        const showBackInStockButton = showStockPermission && shouldOfferBackInStockForProduct({
            featureEnabled: backInStockEnabled,
            variationState,
            activeProduct,
            inventoryPolicy,
        });
        const canManageLineItem = canPurchaseProduct && !showBackInStockButton;
        const canBuildSavedItem = canManageLineItem && effectiveQty > 0;
        const canAddToCart = allowAddToCartPermission && canManageLineItem;
        const canAdjustQuantity = showQuantityColumn && canManageLineItem;
        const canIncreaseQty = !serialQuantityLocked
            && canAdjustQuantity
            && (policyMax === null || effectiveQty + 0.000001 < policyMax || effectiveQty <= 0);
        const canDecreaseQty = !serialQuantityLocked && canAdjustQuantity && effectiveQty > 0;
        const addActionLabel = variationState.isVariableProduct && !variationState.isSelectionValid
            ? 'Select Options'
            : (showBackorderLabelOnAddToCart ? 'Backorder' : 'Add to Cart');
        const wooPlaceholderImage = normalizeImageSource(product.placeholder_image);
        const fallbackImageSrc = wooPlaceholderImage || IMAGE_TRANSPARENT_FALLBACK;
        const imageSrc = normalizeImageSource(product.image) || fallbackImageSrc;
        const productDetailRoute = buildProductDetailRoute(product);
        const fallbackProductUrl = product.url && product.url.trim() !== ''
            ? product.url
            : '';
        const descriptionPreview = truncatePreview(product.description || '', 60);
        const visibleDescriptionPreview = showProductDescription ? descriptionPreview : '';
        const showFavouriteButton = searchResponse?.settings?.show_favourite_button !== false;
        const hasProductSupportContent = visibleDescriptionPreview !== ''
            || (variationState.isVariableProduct && variationAttributes.length > 0);
        const fallbackProductIdentifier = product.sku && product.sku.trim() !== ''
            ? `Item ${product.sku}`
            : `Product #${product.id}`;
        const shouldShowProductFallback = !showProductImage
            && !showProductName
            && !hasProductSupportContent;
        const hasVisibleProductDetails = !showProductImage
            || showProductName
            || shouldShowProductFallback
            || visibleDescriptionPreview !== ''
            || (variationState.isVariableProduct && variationAttributes.length > 0);

        return {
            qty,
            variationState,
            activeProduct,
            selectedUom,
            variationAttributes,
            selectedVariationAttributes,
            variationSelectionMessage,
            serialOptions,
            selectedSerial,
            selectedSerialKey,
            serialLoading,
            serialError,
            serialEnabledForProduct,
            serialForceBuyAll,
            serialDisplayMode,
            serialQuantityLocked,
            effectiveQty,
            inventoryPolicy,
            policyStep,
            policyMax,
            policyAllowZero,
            policyMinNonZero,
            decimalAllowed,
            pricingSnapshot,
            unitPrice,
            currencySymbol,
            currencyCode,
            totalPrice,
            pricingUnavailableMessage,
            isAdding,
            quantityNotice,
            availableUoms,
            showUomSelector,
            rrpValue,
            canPurchaseProduct,
            showBackorder,
            showBackorderLabelOnAddToCart,
            isBackInStockSubmitting,
            isBackInStockSubscribed,
            showEnquiryButton,
            enquiryButtonText,
            showBackInStockButton,
            canBuildSavedItem,
            canAddToCart,
            canAdjustQuantity,
            canIncreaseQty,
            canDecreaseQty,
            addActionLabel,
            fallbackImageSrc,
            imageSrc,
            productDetailRoute,
            fallbackProductUrl,
            visibleDescriptionPreview,
            showFavouriteButton,
            fallbackProductIdentifier,
            shouldShowProductFallback,
            hasVisibleProductDetails,
        };
    };

    const renderProductImage = (product: Product, rowState: ReturnType<typeof getProductRowState>, imageSize: number = 50) => {
        const imageElement = (
            <img
                src={rowState.imageSrc}
                alt={product.name}
                className="rounded border"
                style={{width: `${imageSize}px`, height: `${imageSize}px`, objectFit: 'cover', flexShrink: 0}}
                onError={(evt) => {
                    const target = evt.currentTarget;
                    if (target.dataset.fallbackApplied === '1') {
                        return;
                    }
                    target.dataset.fallbackApplied = '1';
                    target.src = rowState.fallbackImageSrc;
                }}
            />
        );

        if (rowState.productDetailRoute !== '') {
            return (
                <Link
                    to={rowState.productDetailRoute}
                    className="d-inline-flex"
                    aria-label={`View ${product.name}`}
                >
                    {imageElement}
                </Link>
            );
        }

        if (rowState.fallbackProductUrl !== '') {
            return (
                <a
                    href={rowState.fallbackProductUrl}
                    target={fallbackProductLinksOpenInNewTab ? '_blank' : undefined}
                    rel={fallbackProductLinksOpenInNewTab ? 'noopener noreferrer' : undefined}
                    className="d-inline-flex"
                    aria-label={`View ${product.name}`}
                >
                    {imageElement}
                </a>
            );
        }

        return imageElement;
    };

    const renderSerialSelector = (
        product: Product,
        rowState: ReturnType<typeof getProductRowState>,
        mobile: boolean = false
    ) => {
        if (!showQuantityColumn || !rowState.serialEnabledForProduct) {
            return null;
        }

        return (
            <div
                className={mobile ? 'product-result-table__serial-selector product-result-table__serial-selector--mobile' : undefined}
                style={mobile ? undefined : {minWidth: '170px'}}
            >
                {rowState.serialDisplayMode === 'table' ? (
                    <div style={{maxHeight: '180px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px'}}>
                        <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '11px'}}>
                            <thead>
                                <tr>
                                    <th style={{textAlign: 'left', padding: '4px'}}>Use</th>
                                    <th style={{textAlign: 'left', padding: '4px'}}>Serial</th>
                                    {showStockPermission && <th style={{textAlign: 'left', padding: '4px'}}>Qty</th>}
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td style={{padding: '4px'}}>
                                        <input
                                            type="radio"
                                            name={`b2b-serial-${product.id}`}
                                            value=""
                                            checked={rowState.selectedSerialKey === ''}
                                            onChange={() => onSerialChange(product, '')}
                                        />
                                    </td>
                                    <td style={{padding: '4px'}} colSpan={showStockPermission ? 2 : 1}>
                                        {showStockPermission ? 'Use total stock' : 'No specific serial'}
                                    </td>
                                </tr>
                                {rowState.serialOptions.map((option) => {
                                    const optionKey = getSerialOptionKey(option);
                                    return (
                                        <tr key={`${product.id}-${optionKey}`}>
                                            <td style={{padding: '4px'}}>
                                                <input
                                                    type="radio"
                                                    name={`b2b-serial-${product.id}`}
                                                    value={optionKey}
                                                    checked={rowState.selectedSerialKey === optionKey}
                                                    onChange={() => onSerialChange(product, optionKey)}
                                                    disabled={rowState.serialLoading || (rowState.variationState.isVariableProduct && !rowState.variationState.isSelectionValid)}
                                                />
                                            </td>
                                            <td style={{padding: '4px'}}>{option.serial_number}</td>
                                            {showStockPermission && <td style={{padding: '4px'}}>{formatQuantity(option.quantity, true)}</td>}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <select
                        className="form-select form-select-sm"
                        value={rowState.selectedSerialKey}
                        onChange={(evt) => onSerialChange(product, evt.target.value)}
                        disabled={rowState.serialLoading || (rowState.variationState.isVariableProduct && !rowState.variationState.isSelectionValid)}
                    >
                        <option value="">{showStockPermission ? 'Use total stock (no serial)' : 'No specific serial'}</option>
                        {rowState.serialOptions.map((option) => {
                            const optionKey = getSerialOptionKey(option);
                            const label = option.label && option.label !== ''
                                ? option.label
                                : (showStockPermission
                                    ? `${option.serial_number} | Qty ${formatQuantity(option.quantity, true)}`
                                    : option.serial_number);
                            return (
                                <option key={`${product.id}-${optionKey}`} value={optionKey}>
                                    {label}
                                </option>
                            );
                        })}
                    </select>
                )}
                {rowState.serialLoading && (
                    <small className="text-muted d-block mt-1">Loading serials...</small>
                )}
                {!rowState.serialLoading && rowState.serialError !== '' && (
                    <small className="text-danger d-block mt-1">{rowState.serialError}</small>
                )}
                {!rowState.serialLoading && rowState.serialError === '' && rowState.serialOptions.length === 0 && (
                    <small className="text-danger d-block mt-1">No serial stock rows.</small>
                )}
            </div>
        );
    };

    const renderProductSummary = (product: Product, rowState: ReturnType<typeof getProductRowState>, mobile: boolean = false) => (
        <div
            className={[
                'd-flex align-items-start gap-2',
                mobile ? 'product-result-table__mobile-summary' : '',
                mobile && showProductImage ? 'product-result-table__mobile-summary--with-image' : '',
                mobile && !showProductImage ? 'product-result-table__mobile-summary--no-image' : '',
            ].filter(Boolean).join(' ')}
        >
            {showProductImage && (
                <div className={`find-products-table-image-wrap${mobile ? ' product-result-table__mobile-image-wrap' : ''}`}>
                    {rowState.showFavouriteButton && (
                        <FavouriteButton
                            productId={product.id}
                            initialIsFavourite={product.is_favourite === true}
                            displayMode="icon"
                            className="find-products-image-favourite"
                            canToggle={searchResponse?.settings?.favourites_can_toggle === true}
                            ajaxUrl={searchResponse?.settings?.favourites_ajax_url}
                            nonce={searchResponse?.settings?.favourites_nonce}
                            groupsEnabled={searchResponse?.settings?.favourites_groups_enabled === true}
                        />
                    )}
                    {renderProductImage(product, rowState, mobile ? 72 : 50)}
                </div>
            )}
            {rowState.hasVisibleProductDetails && (
                <div className={[
                    showProductImage ? 'flex-grow-1' : '',
                    mobile ? 'product-result-table__mobile-summary-content' : '',
                ].filter(Boolean).join(' ') || undefined}>
                    {!showProductImage && rowState.showFavouriteButton && (
                        <div className="d-flex justify-content-end mb-1">
                            <FavouriteButton
                                productId={product.id}
                                initialIsFavourite={product.is_favourite === true}
                                displayMode="icon"
                                canToggle={searchResponse?.settings?.favourites_can_toggle === true}
                                ajaxUrl={searchResponse?.settings?.favourites_ajax_url}
                                nonce={searchResponse?.settings?.favourites_nonce}
                                groupsEnabled={searchResponse?.settings?.favourites_groups_enabled === true}
                            />
                        </div>
                    )}
                    {showProductName && (
                        rowState.productDetailRoute !== '' ? (
                            <Link to={rowState.productDetailRoute}
                                  className={`text-decoration-none fw-semibold text-dark d-block mb-1${mobile ? ' product-result-table__product-name' : ''}`}
                                  style={{fontSize: mobile ? '1rem' : '0.95rem'}}>
                                {product.name}
                            </Link>
                        ) : rowState.fallbackProductUrl !== '' ? (
                            <a
                                href={rowState.fallbackProductUrl}
                                target={fallbackProductLinksOpenInNewTab ? '_blank' : undefined}
                                rel={fallbackProductLinksOpenInNewTab ? 'noopener noreferrer' : undefined}
                                className={`text-decoration-none fw-semibold text-dark d-block mb-1${mobile ? ' product-result-table__product-name' : ''}`}
                                style={{fontSize: mobile ? '1rem' : '0.95rem'}}
                            >
                                {product.name}
                            </a>
                        ) : (
                            <span
                                className={`fw-semibold text-dark d-block mb-1${mobile ? ' product-result-table__product-name' : ''}`}
                                style={{fontSize: mobile ? '1rem' : '0.95rem'}}
                            >
                                {product.name}
                            </span>
                        )
                    )}
                    {rowState.shouldShowProductFallback && (
                        <small className="text-muted d-block mb-1" style={{fontSize: '0.8rem'}}>
                            {rowState.fallbackProductIdentifier}
                        </small>
                    )}
                    {rowState.visibleDescriptionPreview !== '' && (
                        <small className={`text-muted d-block${mobile ? ' product-result-table__product-description' : ''}`} style={{fontSize: '0.8rem'}}>
                            {rowState.visibleDescriptionPreview}
                        </small>
                    )}
                    {rowState.variationState.isVariableProduct && rowState.variationAttributes.length > 0 && (
                        <div className="mt-2 d-flex flex-column gap-1">
                            {rowState.variationAttributes.map((attribute) => {
                                const selectedValue = normalizeVariationValue(rowState.selectedVariationAttributes[attribute.key]);
                                const attributeLabel = String(attribute.label || attribute.name || attribute.key)
                                    .replace(/^attribute_/, '')
                                    .trim();
                                const optionPromptLabel = attributeLabel !== '' ? attributeLabel : 'option';

                                return (
                                    <select
                                        key={`${product.id}-${attribute.key}`}
                                        className="form-select form-select-sm"
                                        value={selectedValue}
                                        onChange={(evt) => onVariationAttributeChange(product, attribute.key, evt.target.value)}
                                    >
                                        <option value="">{`Choose ${optionPromptLabel}`}</option>
                                        {attribute.options.map((option) => {
                                            const optionValue = normalizeVariationValue(option.value);
                                            const selectable = isVariationOptionSelectable(rowState.variationState, attribute.key, optionValue);
                                            return (
                                                <option
                                                    key={`${product.id}-${attribute.key}-${optionValue}`}
                                                    value={optionValue}
                                                    disabled={!selectable}
                                                >
                                                    {option.label || optionValue}
                                                </option>
                                            );
                                        })}
                                    </select>
                                );
                            })}
                            {rowState.variationSelectionMessage !== '' && (
                                <small className="text-danger" style={{fontSize: '0.72rem'}}>
                                    {rowState.variationSelectionMessage}
                                </small>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    const renderSkuBadge = (product: Product, rowState: ReturnType<typeof getProductRowState>) => {
        const skuBadge = (
            <span className="badge bg-light text-dark border" style={{fontSize: '0.8rem'}}>
                {product.sku || 'N/A'}
            </span>
        );

        if (rowState.productDetailRoute !== '') {
            return <Link to={rowState.productDetailRoute} className="text-decoration-none" aria-label={`View ${product.name}`}>{skuBadge}</Link>;
        }

        if (rowState.fallbackProductUrl !== '') {
            return (
                <a
                    href={rowState.fallbackProductUrl}
                    target={fallbackProductLinksOpenInNewTab ? '_blank' : undefined}
                    rel={fallbackProductLinksOpenInNewTab ? 'noopener noreferrer' : undefined}
                    className="text-decoration-none"
                    aria-label={`View ${product.name}`}
                >
                    {skuBadge}
                </a>
            );
        }

        return skuBadge;
    };

    const renderUomControls = (
        product: Product,
        rowState: ReturnType<typeof getProductRowState>,
        mobile: boolean = false
    ) => (
        <div className={mobile ? 'product-result-table__uom-controls product-result-table__uom-controls--mobile' : 'd-flex flex-column gap-2 align-items-center'}>
            {rowState.showUomSelector ? (
                <select
                    className="form-select form-select-sm"
                    style={mobile ? undefined : {minWidth: '90px'}}
                    value={rowState.selectedUom}
                    onChange={(evt) => onUomChange(product, evt.target.value)}
                >
                    {rowState.availableUoms.map((uom) => (
                        <option key={`${product.id}-${uom}`} value={uom}>{uom}</option>
                    ))}
                </select>
            ) : (
                <span className="badge bg-info text-white" style={{fontSize: '0.8rem'}}>
                    {rowState.selectedUom || 'EA'}
                </span>
            )}
            {renderSerialSelector(product, rowState, mobile)}
        </div>
    );

    const renderPriceBlock = (rowState: ReturnType<typeof getProductRowState>) => (
        <>
            {rowState.pricingUnavailableMessage !== '' ? (
                <small className="text-danger d-block" style={{fontSize: '0.8rem'}}>
                    {rowState.pricingUnavailableMessage}
                </small>
            ) : (
                <>
                    <span className="fw-bold product-result-table__price">
                        {formatMoneyWithCurrencyCode(
                            rowState.unitPrice,
                            rowState.currencyCode,
                            currencyDisplayMode,
                            rowState.currencySymbol,
                        )}
                    </span>
                    {rowState.activeProduct.pricing_enabled === true
                        && rowState.activeProduct.quantity_breaks_enabled === true
                        && rowState.pricingSnapshot.nextBreakQty !== null && (
                        <small className="text-muted d-block" style={{fontSize: '0.75rem'}}>
                            Next {rowState.pricingSnapshot.nextBreakQty}+ {rowState.selectedUom}: {formatMoneyWithCurrencyCode(
                                rowState.pricingSnapshot.nextBreakPrice || rowState.unitPrice,
                                rowState.currencyCode,
                                currencyDisplayMode,
                                rowState.currencySymbol,
                            )}
                        </small>
                    )}
                </>
            )}
        </>
    );

    const renderStockBlock = (product: Product, rowState: ReturnType<typeof getProductRowState>) => (
        <div className="product-result-table__stock-cell">
            {rowState.canPurchaseProduct && rowState.activeProduct.stock !== null && rowState.activeProduct.stock > 0 ? (
                <span className="fw-bold product-result-table__stock-count">
                    {rowState.activeProduct.stock}
                </span>
            ) : rowState.showBackorder ? (
                <span className="badge bg-warning text-dark product-result-table__stock-badge">
                    Backorder
                </span>
            ) : rowState.canPurchaseProduct ? (
                <span className="badge bg-success-subtle text-success border product-result-table__stock-badge">
                    Available
                </span>
            ) : (
                <span className="badge text-danger text-lg-center product-result-table__stock-badge">
                    <i className="fas fa-times-circle"></i>
                </span>
            )}
            <WarehouseSummary
                summary={product.warehouse_summary}
                displayMode={warehouseSummaryRowDisplay}
                title={`${product.name} warehouse availability`}
                triggerLabel={'Warehouses'}
                triggerClassName={'button weka-secondary-btn product-result-table__button product-result-table__button--secondary product-result-table__stock-trigger'}
                className={'product-result-table__stock-summary'}
            />
        </div>
    );

    const renderQuantityControls = (
        product: Product,
        rowState: ReturnType<typeof getProductRowState>,
        mobile: boolean = false
    ) => (
        <div className={mobile ? 'product-result-table__quantity-controls product-result-table__quantity-controls--mobile' : 'd-flex flex-column align-items-center justify-content-center gap-1'}>
            <div className={mobile ? 'product-result-table__quantity-stepper' : 'd-flex align-items-center justify-content-center gap-1'}>
                <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => {
                        const nextQty = rowState.policyAllowZero && rowState.effectiveQty - rowState.policyStep < rowState.policyMinNonZero
                            ? 0
                            : (rowState.effectiveQty - rowState.policyStep);
                        setQuantityForProduct(product, rowState.activeProduct, nextQty, false);
                    }}
                    disabled={!rowState.canDecreaseQty}
                    style={{padding: '0.25rem 0.5rem', fontSize: '0.875rem'}}
                >
                    <i className="fas fa-minus"></i>
                </button>
                <input
                    type="number"
                    className="form-control form-control-sm text-center"
                    style={{width: '64px', fontSize: '0.875rem'}}
                    min={rowState.policyAllowZero ? 0 : rowState.policyMinNonZero}
                    step={rowState.decimalAllowed ? rowState.policyStep : Math.max(1, Math.round(rowState.policyStep))}
                    max={rowState.policyMax !== null ? rowState.policyMax : undefined}
                    value={rowState.effectiveQty}
                    disabled={!rowState.canAdjustQuantity || rowState.serialQuantityLocked}
                    onChange={(evt) => onQuantityChange(evt, product)}
                />
                <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => {
                        const nextQty = rowState.policyAllowZero && rowState.effectiveQty <= 0
                            ? rowState.policyMinNonZero
                            : (rowState.effectiveQty + rowState.policyStep);
                        setQuantityForProduct(product, rowState.activeProduct, nextQty, false);
                    }}
                    disabled={!rowState.canIncreaseQty}
                    style={{padding: '0.25rem 0.5rem', fontSize: '0.875rem'}}
                >
                    <i className="fas fa-plus"></i>
                </button>
            </div>
            {(rowState.quantityNotice !== '' || rowState.variationSelectionMessage !== '' || rowState.inventoryPolicy.step_mode_enabled === true || rowState.policyMax !== null) && (
                <small className="text-muted d-block" style={{fontSize: '0.7rem', lineHeight: 1.3}}>
                    {rowState.quantityNotice !== '' ? rowState.quantityNotice : ''}
                    {rowState.quantityNotice !== '' && (rowState.variationSelectionMessage !== '' || rowState.inventoryPolicy.step_mode_enabled === true || rowState.policyMax !== null) ? ' ' : ''}
                    {rowState.variationSelectionMessage !== '' ? rowState.variationSelectionMessage : ''}
                    {rowState.variationSelectionMessage !== '' && (rowState.inventoryPolicy.step_mode_enabled === true || rowState.policyMax !== null) ? ' ' : ''}
                    {rowState.inventoryPolicy.step_mode_enabled === true
                        ? `MOQ step ${formatQuantity(rowState.policyStep, rowState.decimalAllowed)} ${rowState.selectedUom}.`
                        : ''}
                    {rowState.inventoryPolicy.step_mode_enabled === true && showStockPermission && rowState.policyMax !== null ? ' ' : ''}
                    {showStockPermission && rowState.serialQuantityLocked && rowState.selectedSerial ? `Force serial qty ${formatQuantity(rowState.selectedSerial.quantity, true)}.` : ''}
                    {showStockPermission && rowState.serialQuantityLocked && rowState.selectedSerial && (rowState.inventoryPolicy.step_mode_enabled === true || rowState.policyMax !== null) ? ' ' : ''}
                    {showStockPermission && rowState.policyMax !== null
                        ? (
                            rowState.policyMax <= 0.000001 && rowState.inventoryPolicy.stock_fully_reserved_in_cart === true
                                ? 'No additional quantity available (already in cart).'
                                : `Max ${formatQuantity(rowState.policyMax, rowState.decimalAllowed)} ${rowState.selectedUom}.`
                        )
                        : ''}
                </small>
            )}
        </div>
    );

    const renderActionButtons = (product: Product, rowState: ReturnType<typeof getProductRowState>) => (
        <div className="product-result-table__actions">
            {rowState.showEnquiryButton ? (
                <button
                    type="button"
                    className="button weka-primary-btn product-result-table__button product-result-table__button--primary weka-enquire-now-btn"
                    data-product-id={product.id}
                    data-product-name={product.name}
                >
                    <i className="fas fa-envelope" aria-hidden="true"></i>
                    <span>{rowState.enquiryButtonText}</span>
                </button>
            ) : rowState.showBackInStockButton ? (
                <button
                    type="button"
                    className="button weka-primary-btn product-result-table__button product-result-table__button--primary"
                    onClick={() => {
                        void subscribeToBackInStock(product.id);
                    }}
                    disabled={rowState.isBackInStockSubmitting}
                >
                    {rowState.isBackInStockSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm" role="status"></span>
                            <span>Saving...</span>
                        </>
                    ) : (
                        <span>{rowState.isBackInStockSubscribed ? backInStockRemoveButtonLabel : backInStockButtonLabel}</span>
                    )}
                </button>
            ) : allowAddToCartPermission ? (
                <button
                    type="button"
                    className="button weka-add-to-cart-button product-result-table__button product-result-table__button--primary"
                    onClick={() => addToCart(product.id, rowState.effectiveQty)}
                    disabled={rowState.isAdding || rowState.effectiveQty <= 0 || !rowState.canAddToCart}
                >
                    {rowState.isAdding ? (
                        <>
                            <span className="spinner-border spinner-border-sm" role="status"></span>
                            <span>Adding...</span>
                        </>
                    ) : (
                        <>
                            <i className="fas fa-cart-plus"></i>
                            <span>{rowState.addActionLabel}</span>
                        </>
                    )}
                </button>
            ) : null}
            {(canShowDraftOrderButton || canShowQuoteButton) && (
                <div className="product-result-table__secondary-actions">
                    {canShowDraftOrderButton && (
                        <button
                            type="button"
                            className="button weka-secondary-btn product-result-table__button product-result-table__button--secondary product-result-table__button--draft"
                            onClick={() => openDraftSelector(product.id)}
                            disabled={
                                rowState.isAdding
                                || rowState.effectiveQty <= 0
                                || !rowState.canBuildSavedItem
                            }
                            aria-label="Add to Draft Order"
                        >
                            Save Draft
                        </button>
                    )}
                    {canShowQuoteButton && (
                        <button
                            type="button"
                            className="button weka-secondary-btn product-result-table__button product-result-table__button--secondary product-result-table__button--quote"
                            onClick={() => openQuoteSelector(product.id)}
                            disabled={
                                rowState.isAdding
                                || rowState.effectiveQty <= 0
                                || !rowState.canBuildSavedItem
                            }
                            aria-label="Add to Quote"
                        >
                            Save Quote
                        </button>
                    )}
                </div>
            )}
        </div>
    );

    const tableContent = (
        <>
            {!detailMode && showToolbar && (
                <div className={embedded ? 'mb-3' : 'card-header bg-white'}>
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <div className="d-flex align-items-center gap-2">
                                <label className="mb-0 fw-semibold small">Sort:</label>
                                <select className="form-select form-select-sm" onChange={handleSortFilterChange} value={sortSelectValue} style={{minWidth: '140px', fontSize: '0.875rem'}}>
                                    <option value="">Default</option>
                                    <option value={'relevance'}>Relevant</option>
                                    {showPricePermission && <option value={'price_low_to_high'}>Price ↑</option>}
                                    {showPricePermission && <option value={'price_high_to_low'}>Price ↓</option>}
                                    <option value={'title_a_to_z'}>A-Z</option>
                                    <option value={'title_z_to_a'}>Z-A</option>
                                </select>
                            </div>
                            {showAddAllButton && (
                                <button type="button"
                                        className="btn btn-primary"
                                        onClick={addAllToCart}
                                        style={{fontSize: '0.875rem'}}>
                                    <i className="fas fa-cart-plus"></i>
                                    <span className="d-none d-sm-inline ms-1">Add All</span>
                                </button>
                            )}
                        </div>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <span className="text-muted small">
                                <strong>{searchResponse?.total_results ?? 0}</strong> found
                            </span>
                            <BulkAddToCart templateCsvUrl={templateCsvUrl} showUploadButton={showUploadCsv} />
                        </div>
                    </div>
                </div>
            )}
            <div className={embedded ? '' : 'card-body p-0'}>
                {!searchResponse ? (
                    <div className="p-3">
                        <SkeletonLoader type="table" rows={10} columns={skeletonColumnCount} />
                    </div>
                ) : searchResponse.results.length < 1 ? (
                    <div className="text-center py-5">
                        <i className="fas fa-search fa-3x text-muted mb-3"></i>
                        <p className="text-muted">{emptyMessage}</p>
                    </div>
                ) : (
                    <>
                    <div className="table-responsive product-result-table__desktop-table">
                        <table className="table table-hover mb-0 align-middle p-3">
                            <thead className="table-light">
                                <tr>
                                    <th className="text-start" style={{minWidth: productColumnMinWidth, width: productColumnWidth, whiteSpace: 'nowrap'}}>Product</th>
                                    {showSkuColumn && (
                                        <th className="text-center" style={{width: skuColumnWidth}}>SKU</th>
                                    )}
                                    {showUomColumn && (
                                        <th className="text-center" style={{width: uomColumnWidth}}>UOM</th>
                                    )}
                                    {showRrpColumn && (
                                        <th className="text-end" style={{width: rrpColumnWidth}}>RRP</th>
                                    )}
                                    {showPriceColumn && (
                                        <th className="text-end" style={{width: priceColumnWidth}}>Price</th>
                                    )}
                                    {showStockColumn && (
                                        <th className="text-center" style={{width: stockColumnWidth}}>Stock</th>
                                    )}
                                    {showQuantityColumn && (
                                        <th className="text-center" style={{width: quantityColumnWidth}}>Quantity</th>
                                    )}
                                    {showTotalColumn && (
                                        <th className="text-end" style={{width: totalColumnWidth}}>Total</th>
                                    )}
                                    {showActionColumn && (
                                        <th className="text-center" style={{width: actionColumnWidth, minWidth: actionColumnWidth}}>Action</th>
                                    )}
                                </tr>
                            </thead>
                                <tbody>
                                {searchResponse?.results.map((product: Product) => {
                                    const rowState = getProductRowState(product);
                                    const {
                                        variationState,
                                        activeProduct,
                                        selectedUom,
                                        variationAttributes,
                                        selectedVariationAttributes,
                                        variationSelectionMessage,
                                        serialQuantityLocked,
                                        effectiveQty,
                                        policyStep,
                                        policyMax,
                                        policyAllowZero,
                                        policyMinNonZero,
                                        decimalAllowed,
                                        pricingSnapshot,
                                        unitPrice,
                                        currencySymbol,
                                        totalPrice,
                                        canIncreaseQty,
                                        canDecreaseQty,
                                        canAdjustQuantity,
                                        rrpValue,
                                        canPurchaseProduct,
                                        showBackorder,
                                        quantityNotice,
                                        selectedSerial,
                                        showFavouriteButton,
                                        shouldShowProductFallback,
                                        hasVisibleProductDetails,
                                        productDetailRoute,
                                        fallbackProductUrl,
                                        visibleDescriptionPreview,
                                    } = rowState;

                                    return <tr key={product.id} className="border-bottom">
                                        <td className="text-start py-3" style={{width: productColumnWidth, minWidth: productColumnMinWidth}}>
                                            {renderProductSummary(product, rowState)}
                                        </td>
                                        {showSkuColumn && (
                                            <td className="text-center py-3">
                                                {renderSkuBadge(product, rowState)}
                                            </td>
                                        )}
                                        {showUomColumn && (
                                            <td className="text-center py-3">
                                                {renderUomControls(product, rowState)}
                                        </td>
                                        )}
                                        {showRrpColumn && (
                                            <td className="text-end text-muted py-3">
                                            {rrpValue > 0 && rrpValue > unitPrice
                                                ? renderRrpValue(
                                                    rrpValue,
                                                    rowState.currencyCode,
                                                    rowState.currencySymbol,
                                                    showRrpStrikethrough,
                                                )
                                                : <span style={{fontSize: '0.85rem'}}>-</span>}
                                        </td>
                                        )}
                                        {showPriceColumn && (
                                            <td className="text-end py-3">
                                            {renderPriceBlock(rowState)}
                                        </td>
                                        )}
                                        {showStockColumn && (
                                        <td className="text-center py-3">
                                            {renderStockBlock(product, rowState)}
                                        </td>
                                        )}
                                        {showQuantityColumn && (
                                            <td className="text-center py-3">
                                                {renderQuantityControls(product, rowState)}
                                            </td>
                                        )}
                                        {showTotalColumn && (
                                            <td className="text-end py-3">
                                            {rowState.pricingUnavailableMessage !== '' ? (
                                                <small className="text-danger d-block" style={{fontSize: '0.8rem'}}>
                                                    {rowState.pricingUnavailableMessage}
                                                </small>
                                            ) : (
                                                <span className="fw-bold" style={{fontSize: '1rem'}}>
                                                    {totalPrice !== null
                                                        ? formatMoneyWithCurrencyCode(
                                                            totalPrice,
                                                            rowState.currencyCode,
                                                            currencyDisplayMode,
                                                            rowState.currencySymbol,
                                                        )
                                                        : '-'}
                                                </span>
                                            )}
                                        </td>
                                        )}
                                        {showActionColumn && (
                                            <td className="text-center py-3">
                                                {renderActionButtons(product, rowState)}
                                            </td>
                                        )}
                                    </tr>;
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div className="product-result-table__mobile-cards">
                        {searchResponse?.results.map((product: Product) => {
                            const rowState = getProductRowState(product);

                            return (
                                <article key={`mobile-${product.id}`} className="product-result-table__mobile-card">
                                    <div className="product-result-table__mobile-card-header">
                                        {renderProductSummary(product, rowState, true)}
                                        {showSkuColumn && (
                                            <div className="product-result-table__mobile-card-meta">
                                                <span className="product-result-table__mobile-label">SKU</span>
                                                <div>{renderSkuBadge(product, rowState)}</div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="product-result-table__mobile-card-body">
                                        {showUomColumn && (
                                            <div className="product-result-table__mobile-card-section product-result-table__mobile-card-section--uom">
                                                <span className="product-result-table__mobile-label">UOM</span>
                                                {renderUomControls(product, rowState, true)}
                                            </div>
                                        )}
                                        {showRrpColumn && (
                                            <div className="product-result-table__mobile-card-meta product-result-table__mobile-card-meta--rrp">
                                                <span className="product-result-table__mobile-label">RRP</span>
                                                <div className="text-muted">
                                                    {rowState.rrpValue > 0 && rowState.rrpValue > rowState.unitPrice
                                                        ? renderRrpValue(
                                                            rowState.rrpValue,
                                                            rowState.currencyCode,
                                                            rowState.currencySymbol,
                                                            showRrpStrikethrough,
                                                        )
                                                        : <span style={{fontSize: '0.85rem'}}>-</span>}
                                                </div>
                                            </div>
                                        )}
                                        {showPriceColumn && (
                                            <div className="product-result-table__mobile-card-meta product-result-table__mobile-card-meta--price">
                                                <span className="product-result-table__mobile-label">Price</span>
                                                <div>{renderPriceBlock(rowState)}</div>
                                            </div>
                                        )}
                                        {showStockColumn && (
                                            <div className="product-result-table__mobile-card-section product-result-table__mobile-card-section--stock">
                                                <span className="product-result-table__mobile-label">Stock</span>
                                                {renderStockBlock(product, rowState)}
                                            </div>
                                        )}
                                        {showQuantityColumn && (
                                            <div className="product-result-table__mobile-card-section product-result-table__mobile-card-section--quantity">
                                                <span className="product-result-table__mobile-label">Quantity</span>
                                                {renderQuantityControls(product, rowState, true)}
                                            </div>
                                        )}
                                        {showTotalColumn && (
                                            <div className="product-result-table__mobile-card-meta product-result-table__mobile-card-meta--total">
                                                <span className="product-result-table__mobile-label">Total</span>
                                                {rowState.pricingUnavailableMessage !== '' ? (
                                                    <div className="text-danger" style={{fontSize: '0.8rem'}}>
                                                        {rowState.pricingUnavailableMessage}
                                                    </div>
                                                ) : (
                                                    <div className="fw-bold">
                                                        {rowState.totalPrice !== null
                                                            ? formatMoneyWithCurrencyCode(
                                                                rowState.totalPrice,
                                                                rowState.currencyCode,
                                                                currencyDisplayMode,
                                                                rowState.currencySymbol,
                                                            )
                                                            : '-'}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {showActionColumn && (
                                        <div className="product-result-table__mobile-card-actions">
                                            {renderActionButtons(product, rowState)}
                                        </div>
                                    )}
                                </article>
                            );
                        })}
                    </div>
                    </>
                )}
            </div>
            {!detailMode && searchResponse && searchResponse.total_pages > 1 && (
                <div className={embedded ? 'bg-white pt-3' : 'card-footer bg-white'}>
                    <PaginationButtons
                        page={searchResponse?.current_page ?? 0}
                        totalPages={searchResponse?.total_pages ?? 0}
                        onChangePage={onPageChange}
                    />
                </div>
            )}
            {canShowDraftOrderButton && (
                <DraftOrderSelectorModal
                    isOpen={draftSelectorState !== null}
                    onClose={() => setDraftSelectorState(null)}
                    itemPayload={draftSelectorState?.payload || null}
                    itemName={draftSelectorState ? (getProductById(draftSelectorState.productId)?.name || '') : ''}
                    canCreateDraft={canCreateDraftOrders}
                    canAddToExisting={canEditDraftItems}
                    onSuccess={() => {
                        if (draftSelectorState) {
                            resetProductSelectionAfterSuccess(draftSelectorState.productId);
                        }
                    }}
                />
            )}
            {canShowQuoteButton && (
                <QuoteSelectorModal
                    isOpen={quoteSelectorState !== null}
                    onClose={() => setQuoteSelectorState(null)}
                    itemPayload={quoteSelectorState?.payload || null}
                    itemName={quoteSelectorState ? (getProductById(quoteSelectorState.productId)?.name || '') : ''}
                    canCreateQuote={canCreateQuotes}
                    canAddToExisting={canEditQuotes}
                    onSuccess={() => {
                        if (quoteSelectorState) {
                            resetProductSelectionAfterSuccess(quoteSelectorState.productId);
                        }
                    }}
                />
            )}
        </>
    );

    return <div className={`product-result-table${embedded ? ' product-result-table--embedded' : ''}${compactDesktop ? ' product-result-table--compact-desktop' : ''}`}>
        {embedded ? tableContent : (
            <MyAccountCard
                variant="page"
                slug="product-results"
                wrapContent={false}
            >
                {tableContent}
            </MyAccountCard>
        )}
    </div>;
};

export default ProductResultTable;
